import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Package, RefreshCw, Play, CheckCircle, Clock, AlertCircle, Search, Plus, Zap, UserPlus, Flag, SkipForward, RotateCcw, Warehouse, Wrench, Eye, ShieldCheck, Database, Printer, Unlock, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FGPutAwaySupervisor = () => {
  const navigate = useNavigate();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('staff');
  
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    slips: { pending: 0, assigned: 0, in_progress: 0, completed_today: 0 },
    available_staff: 0,
    pieces_awaiting_put_away: 0
  });
  
  // Staff list for selection
  const [staffList, setStaffList] = useState([]);
  const [staffSearch, setStaffSearch] = useState('');
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  
  // Available staff (today's selections)
  const [availableStaff, setAvailableStaff] = useState([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  
  // Put Away Slips
  const [slips, setSlips] = useState([]);
  const [isLoadingSlips, setIsLoadingSlips] = useState(false);
  
  // Staff stats
  const [staffStats, setStaffStats] = useState([]);
  
  // Assign dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [selectedStaffForAssign, setSelectedStaffForAssign] = useState('');
  
  // Reassign dialog
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [selectedStaffForReassign, setSelectedStaffForReassign] = useState('');
  
  // Slip detail dialog
  const [slipDetailOpen, setSlipDetailOpen] = useState(false);
  const [viewingSlip, setViewingSlip] = useState(null);
  
  // Loading states for buttons to prevent double-clicks
  const [isGeneratingSlip, setIsGeneratingSlip] = useState(false);
  const [isGeneratingGTSlip, setIsGeneratingGTSlip] = useState(false);
  const [isAutoAllocating, setIsAutoAllocating] = useState(false);
  
  // GT Slips (separate tab)
  const [gtSlips, setGTSlips] = useState([]);
  const [gtSlipsStats, setGTSlipsStats] = useState({ pending: 0, assigned: 0, in_progress: 0, completed: 0, total: 0 });
  const [isLoadingGTSlips, setIsLoadingGTSlips] = useState(false);
  
  // Skipped Items Queue
  const [skippedItems, setSkippedItems] = useState([]);
  const [skippedCounts, setSkippedCounts] = useState({ pending_review: 0, reassigned: 0, returned_to_sfg: 0, total: 0 });
  const [isLoadingSkipped, setIsLoadingSkipped] = useState(false);
  const [selectedSkippedItem, setSelectedSkippedItem] = useState(null);
  const [skippedActionDialog, setSkippedActionDialog] = useState(null); // 'reassign' or 'return_sfg'
  const [skippedActionNotes, setSkippedActionNotes] = useState('');
  const [isProcessingSkipped, setIsProcessingSkipped] = useState(false);

  // Reset In-Progress Slips
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [inProgressSummary, setInProgressSummary] = useState(null);
  const [isLoadingInProgress, setIsLoadingInProgress] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetHours, setResetHours] = useState(2);

  // Migration state
  const [isReconciling, setIsReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [isReconcilingV1PutAway, setIsReconcilingV1PutAway] = useState(false);
  const [reconcileV1PutAwayResult, setReconcileV1PutAwayResult] = useState(null);
  
  // Design transfer barcode fix state

  // Repair completed slips state
  const [isRepairing, setIsRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [isRecreatingIndexes, setIsRecreatingIndexes] = useState(false);
  
  // Diagnose specific slip state
  const [diagnoseSlipId, setDiagnoseSlipId] = useState('');
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  const [isDowngrading, setIsDowngrading] = useState(false);
  const [downgradeResult, setDowngradeResult] = useState(null);

  // Load data on mount and set up auto-refresh
  useEffect(() => {
    fetchStaffList();
    fetchAvailableStaff();
    fetchDashboardStats();
    fetchSlips();
    fetchStaffStats();
    fetchGTSlips();
    fetchSkippedItems();
    
    // Auto-refresh every 10 seconds to catch auto-generated slips
    const refreshInterval = setInterval(() => {
      fetchDashboardStats();
      fetchSlips();
      fetchStaffStats();
      fetchSkippedItems();
    }, 10000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  const fetchStaffList = async (search = '') => {
    setIsLoadingStaff(true);
    try {
      // Use the same endpoint as Dispatch Supervisor
      const url = search 
        ? `/wms/dispatch/staff-list?search=${encodeURIComponent(search)}`
        : '/wms/dispatch/staff-list';
      const response = await apiClient.get(url);
      setStaffList(response.data.staff || []);
    } catch (error) {
      console.error('Failed to load staff list:', error);
      toast.error('Failed to load staff list');
    } finally {
      setIsLoadingStaff(false);
    }
  };

  const fetchAvailableStaff = async () => {
    try {
      const response = await apiClient.get('/wms/fg/put-away/available-staff');
      const available = response.data.available_staff || [];
      setAvailableStaff(available);
      setSelectedStaffIds(available.map(s => s.staff_id));
    } catch (error) {
      console.error('Failed to load available staff:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await apiClient.get('/wms/fg/put-away/dashboard-stats');
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const fetchSlips = async (status = null) => {
    setIsLoadingSlips(true);
    try {
      const params = status ? `?status=${status}` : '';
      const response = await apiClient.get(`/wms/fg/put-away/slips${params}`);
      setSlips(response.data.slips || []);
    } catch (error) {
      console.error('Error loading slips:', error);
      toast.error('Failed to load slips');
    } finally {
      setIsLoadingSlips(false);
    }
  };

  const fetchStaffStats = async () => {
    try {
      const response = await apiClient.get('/wms/fg/put-away/staff-stats');
      setStaffStats(response.data.stats || []);
    } catch (error) {
      console.error('Failed to load staff stats:', error);
    }
  };

  const fetchGTSlips = async (status = null) => {
    setIsLoadingGTSlips(true);
    try {
      const params = status ? `?status=${status}` : '';
      const response = await apiClient.get(`/wms/fg/put-away/goods-transfer-slips${params}`);
      setGTSlips(response.data.slips || []);
      setGTSlipsStats(response.data.stats || { pending: 0, assigned: 0, in_progress: 0, completed: 0, total: 0 });
    } catch (error) {
      console.error('Error loading GT slips:', error);
    } finally {
      setIsLoadingGTSlips(false);
    }
  };

  const handleSearchStaff = () => {
    fetchStaffList(staffSearch);
  };

  const handleToggleStaff = (staffId) => {
    setSelectedStaffIds(prev => 
      prev.includes(staffId) 
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  const handleSaveStaff = async () => {
    try {
      await apiClient.post('/wms/fg/put-away/set-available-staff', selectedStaffIds);
      toast.success(`${selectedStaffIds.length} staff set as available for Put Away`);
      fetchAvailableStaff();
      fetchDashboardStats();
      fetchStaffStats();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save staff selection');
    }
  };

  const handleGenerateSlip = async () => {
    if (isGeneratingSlip) return; // Prevent double-clicks
    setIsGeneratingSlip(true);
    try {
      const response = await apiClient.post('/wms/fg/put-away/generate-slip', null, {
        params: { max_pieces: 120 }
      });
      toast.success(`Generated slip ${response.data.slip.slip_id} with ${response.data.slip.total_pieces} pieces`);
      fetchSlips();
      fetchDashboardStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate slip');
    } finally {
      setIsGeneratingSlip(false);
    }
  };

  const handleGenerateGTSlip = async () => {
    if (isGeneratingGTSlip) return; // Prevent double-clicks
    setIsGeneratingGTSlip(true);
    try {
      const response = await apiClient.post('/wms/fg/put-away/generate-slip-goods-transfer', null, {
        params: { max_bundles: 10, auto_assign: true }
      });
      // New response format: one slip per bundle
      const slipsGenerated = response.data.slips_generated || 1;
      toast.success(`Generated ${slipsGenerated} GT Put Away slip(s) - one per bundle`);
      fetchSlips();
      fetchDashboardStats();
      fetchGTSlips(); // Refresh GT slips tab
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate Goods Transfer slip');
    } finally {
      setIsGeneratingGTSlip(false);
    }
  };

  const handleAutoAllocate = async () => {
    if (isAutoAllocating) return; // Prevent double-clicks
    setIsAutoAllocating(true);
    try {
      const response = await apiClient.post('/wms/fg/put-away/auto-allocate');
      toast.success(`Auto-allocated ${response.data.allocated} slips`);
      fetchSlips();
      fetchDashboardStats();
      fetchStaffStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to auto-allocate');
    } finally {
      setIsAutoAllocating(false);
    }
  };

  // Skipped Items Functions
  const fetchSkippedItems = async () => {
    setIsLoadingSkipped(true);
    try {
      const response = await apiClient.get('/wms/fg/put-away/skipped-items');
      setSkippedItems(response.data.skipped_items || []);
      setSkippedCounts(response.data.counts || { pending_review: 0, reassigned: 0, returned_to_sfg: 0, total: 0 });
    } catch (error) {
      console.error('Failed to load skipped items:', error);
    } finally {
      setIsLoadingSkipped(false);
    }
  };

  const handleReassignSkipped = async () => {
    if (!selectedSkippedItem) return;
    setIsProcessingSkipped(true);
    try {
      const response = await apiClient.post(
        `/wms/fg/put-away/skipped-items/${selectedSkippedItem.skipped_id}/reassign`,
        null,
        { params: { notes: skippedActionNotes || null } }
      );
      toast.success(response.data.message);
      setSkippedActionDialog(null);
      setSelectedSkippedItem(null);
      setSkippedActionNotes('');
      fetchSkippedItems();
      fetchSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign');
    } finally {
      setIsProcessingSkipped(false);
    }
  };

  const handleReturnToSFG = async () => {
    if (!selectedSkippedItem) return;
    setIsProcessingSkipped(true);
    try {
      const response = await apiClient.post(
        `/wms/fg/put-away/skipped-items/${selectedSkippedItem.skipped_id}/return-to-sfg`,
        null,
        { params: { notes: skippedActionNotes || null } }
      );
      toast.success(response.data.message);
      setSkippedActionDialog(null);
      setSelectedSkippedItem(null);
      setSkippedActionNotes('');
      fetchSkippedItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return to SFG');
    } finally {
      setIsProcessingSkipped(false);
    }
  };

  // Fetch In-Progress Slips Summary
  const fetchInProgressSummary = async () => {
    setIsLoadingInProgress(true);
    try {
      const response = await apiClient.get('/wms/fg/put-away/in-progress-slips-summary');
      setInProgressSummary(response.data.summary);
    } catch (error) {
      console.error('Failed to load in-progress summary:', error);
      toast.error('Failed to load in-progress slips summary');
    } finally {
      setIsLoadingInProgress(false);
    }
  };

  // Open Reset Dialog
  const openResetDialog = () => {
    fetchInProgressSummary();
    setResetDialogOpen(true);
  };

  // Handle Reset In-Progress Slips
  const handleResetInProgressSlips = async (slipId = null) => {
    setIsResetting(true);
    try {
      const params = slipId 
        ? { slip_id: slipId }
        : { older_than_hours: resetHours };
      
      const response = await apiClient.post('/wms/fg/put-away/reset-in-progress-slips', null, { params });
      
      if (response.data.reset_count > 0) {
        toast.success(`Reset ${response.data.reset_count} slip(s) with ${response.data.total_pieces} pieces`);
      } else {
        toast.info('No slips found to reset');
      }
      
      // Refresh data
      fetchInProgressSummary();
      fetchSlips();
      fetchDashboardStats();
      fetchStaffStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset slips');
    } finally {
      setIsResetting(false);
    }
  };

  const handleAssignStaff = async () => {
    if (!selectedSlip || !selectedStaffForAssign) return;
    
    try {
      await apiClient.post(`/wms/fg/put-away/assign-slip/${selectedSlip.slip_id}`, {
        staff_id: selectedStaffForAssign
      });
      toast.success('Staff assigned successfully');
      setAssignDialogOpen(false);
      setSelectedStaffForAssign('');
      fetchSlips();
      fetchDashboardStats();
      fetchStaffStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign staff');
    }
  };

  const handleReassignStaff = async () => {
    if (!selectedSlip || !selectedStaffForReassign) return;
    
    try {
      await apiClient.post(`/wms/fg/put-away/reassign-slip/${selectedSlip.slip_id}`, {
        staff_id: selectedStaffForReassign
      });
      toast.success(`Slip reassigned from ${selectedSlip.assigned_to_name || selectedSlip.assigned_to} to new staff`);
      setReassignDialogOpen(false);
      setSelectedStaffForReassign('');
      fetchSlips();
      fetchDashboardStats();
      fetchStaffStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign staff');
    }
  };

  // Migration functions


  const handleReconcileV1PutAway = async (dryRun) => {
    setIsReconcilingV1PutAway(true);
    setReconcileV1PutAwayResult(null);
    try {
      const response = await apiClient.post(
        '/combo/assembly/tools/reconcile-v1-consumed-vs-putaway',
        { dry_run: dryRun }
      );
      setReconcileV1PutAwayResult(response.data);
      const msg = dryRun
        ? `Dry-run: would heal ${response.data.total_healed} USN(s) across ${response.data.sessions_with_conflicts} session(s).`
        : `Healed ${response.data.total_healed} USN(s) across ${response.data.sessions_with_conflicts} session(s).`;
      toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'V1 put-away reconcile failed');
    } finally {
      setIsReconcilingV1PutAway(false);
    }
  };

  const handleReconcileSlips = async () => {    setIsReconciling(true);
    setReconcileResult(null);
    try {
      const response = await apiClient.post('/wms/fg/put-away/reconcile-slips');
      setReconcileResult(response.data);
      toast.success(response.data.message);
      // Refresh slips list
      fetchSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setIsReconciling(false);
    }
  };

  // Design Transfer Barcode Fix functions
  const [isReconcilingWip, setIsReconcilingWip] = useState(false);
  const [wipReconcileResult, setWipReconcileResult] = useState(null);

  // Mismatched Inhouse Bundle Receipts (USN count vs len(usn_codes))
  const [isLoadingMismatched, setIsLoadingMismatched] = useState(false);
  const [isToppingUpMismatched, setIsToppingUpMismatched] = useState(false);
  const [mismatchedResult, setMismatchedResult] = useState(null);
  const [mismatchedApplyResult, setMismatchedApplyResult] = useState(null);

  // GS children metadata backfill (item_index + source_bundle_barcode)

  // Refresh Inventory Item Details
  const [isRefreshingInventory, setIsRefreshingInventory] = useState(false);
  const [inventoryRefreshResult, setInventoryRefreshResult] = useState(null);
  // 2026-05-14 — Ensure Re-Sticker perf indexes (FG Re-Sticker Queue
  // was timing out at 20+ stickers because 2 nested-array paths were
  // unindexed in production). Auto-creates on startup; this button
  // covers multi-site DBs and lets the operator self-verify.
  const [isEnsuringIndexes, setIsEnsuringIndexes] = useState(false);
  const [ensureIndexesResult, setEnsureIndexesResult] = useState(null);
  const [isDedupingGreige, setIsDedupingGreige] = useState(false);
  const [greigeDedupeResult, setGreigeDedupeResult] = useState(null);
  const [isHealingInhouse, setIsHealingInhouse] = useState(false);
  const [inhouseHealResult, setInhouseHealResult] = useState(null);
  const [isHealingSfgSku, setIsHealingSfgSku] = useState(false);
  const [sfgSkuHealResult, setSfgSkuHealResult] = useState(null);
  // Backfill pack_barcode from usn_code (Re-Sticker Queue repair)
  const [isBackfillingPackBarcode, setIsBackfillingPackBarcode] = useState(false);
  const [backfillPackBarcodeResult, setBackfillPackBarcodeResult] = useState(null);
  // Heal stuck PICKED_FOR_COMBO_V2 bundles (Combo V2 cancel didn't revert)
  const [isHealingV2Bundles, setIsHealingV2Bundles] = useState(false);
  const [healV2BundlesResult, setHealV2BundlesResult] = useState(null);

  // Heal phantom FG inventory rows whose USN has been deactivated by a
  // replacement (2026-05-11). Operators put-away the OLD half-printed
  // sticker and the row stayed STORED forever after picking moved on to
  // the canonical replaced USN. This admin tool finds those rows and
  // either renames them to canonical (when canonical has no row of its
  // own) or marks them VOID_REPLACED (when picking already happened).
  const [isHealingPhantom, setIsHealingPhantom] = useState(false);
  const [healPhantomResult, setHealPhantomResult] = useState(null);
  const [isFixingDispatchReceipts, setIsFixingDispatchReceipts] = useState(false);
  const [dispatchReceiptResult, setDispatchReceiptResult] = useState(null);
  const [isResettingAssigned, setIsResettingAssigned] = useState(false);
  const [resetAssignedResult, setResetAssignedResult] = useState(null);
  const [isCleaningGhosts, setIsCleaningGhosts] = useState(false);
  const [ghostCleanupResult, setGhostCleanupResult] = useState(null);

  // Backfill dispatch_pieces_processed for table clearance

  // Inhouse bundle full-sync (permanent fix for design-transferred bundles)
  // Heal split USN replacement chains (one-time historical fix)

  // Stuck pick slips (SFG queue) — Force Release admin tool
  const [stuckMinHours, setStuckMinHours] = useState(12);
  const [stuckZeroOnly, setStuckZeroOnly] = useState(true);
  const [isLoadingStuck, setIsLoadingStuck] = useState(false);
  const [stuckResult, setStuckResult] = useState(null);
  const [releasingSlipNo, setReleasingSlipNo] = useState(null);

  // Orphan COMBO_ASSEMBLY pick slips — reconcile tool
  const [isLoadingOrphans, setIsLoadingOrphans] = useState(false);
  const [orphanResult, setOrphanResult] = useState(null);
  const [reconcilingOrphanSlipNo, setReconcilingOrphanSlipNo] = useState(null);
  const [isBulkReconcilingOrphans, setIsBulkReconcilingOrphans] = useState(false);
  const [bulkOrphanResult, setBulkOrphanResult] = useState(null);

  // Heal SFG SKU collision — admin tool
  const [healBundleCode, setHealBundleCode] = useState('');
  const [healReason, setHealReason] = useState('');
  const [healPreview, setHealPreview] = useState(null);
  const [healResult, setHealResult] = useState(null);
  const [isHealingSku, setIsHealingSku] = useState(false);

  // Heal Historical Reprint Slips — chunked
  const [isHealingReprints, setIsHealingReprints] = useState(false);
  const [reprintHealCandidateCount, setReprintHealCandidateCount] = useState(null);
  const [reprintHealProgress, setReprintHealProgress] = useState({
    processed: 0,
    slips_rewritten: 0,
    usns_rewritten: 0,
    running: false,
    dry_run: true,
    done: false,
    error: null,
  });
  const [reprintHealLog, setReprintHealLog] = useState([]);

  // Combo component picked_bundles mismatch (active combo groups only)
  const [isScanningComboMismatch, setIsScanningComboMismatch] = useState(false);
  const [isFixingComboMismatch, setIsFixingComboMismatch] = useState(false);
  const [comboMismatchResult, setComboMismatchResult] = useState(null);
  const [comboMismatchFixResult, setComboMismatchFixResult] = useState(null);

  // Bundle consumption_by_session mismatch vs pick-slip required_qty
  const [isScanningConsumption, setIsScanningConsumption] = useState(false);
  const [isFixingConsumption, setIsFixingConsumption] = useState(false);
  const [consumptionResult, setConsumptionResult] = useState(null);
  const [consumptionFixResult, setConsumptionFixResult] = useState(null);

  // DOC ↔ IT Duplicate reconcile (P0 2026-03)
  const [isScanningDocIt, setIsScanningDocIt] = useState(false);
  const [isFixingDocIt, setIsFixingDocIt] = useState(false);
  const [docItScan, setDocItScan] = useState(null);
  const [docItFix, setDocItFix] = useState(null);

  const handleScanDocIt = async () => {
    setIsScanningDocIt(true);
    setDocItFix(null);
    try {
      const res = await apiClient.get('/wms/queue-admin/doc-it-duplicates');
      setDocItScan(res.data);
      if (res.data.total_mismatched === 0) {
        toast.success('No DOC / IT duplicates out of sync');
      } else {
        toast.info(`${res.data.total_mismatched} linked DOC/IT pair(s) need mirroring`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setIsScanningDocIt(false);
    }
  };

  const handleFixDocIt = async () => {
    if (!window.confirm(
      `Mirror DOC → IT state on ${docItScan?.total_mismatched || 0} linked pair(s)?\n\n` +
      `This copies bundle_receipt_completed, received_bundles, and status from the ` +
      `consignment onto its linked internal_transfer (DOC wins). Historical records only — ` +
      `new linkages mirror automatically going forward.`
    )) return;
    setIsFixingDocIt(true);
    try {
      const res = await apiClient.post('/wms/queue-admin/reconcile-doc-it-duplicates?dry_run=false');
      setDocItFix(res.data);
      toast.success(`Reconciled ${res.data.fixed} pair(s)`);
      await handleScanDocIt();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconcile failed');
    } finally {
      setIsFixingDocIt(false);
    }
  };

  const handleScanConsumption = async () => {
    setIsScanningConsumption(true);
    setConsumptionFixResult(null);
    try {
      const res = await apiClient.get('/wms/queue-admin/bundle-consumption-mismatch');
      setConsumptionResult(res.data);
      if (res.data.total_mismatched === 0) {
        toast.success('No consumption mismatches found');
      } else {
        toast.info(`${res.data.total_mismatched} bundle/session consumption rows disagree with their pick slip`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setIsScanningConsumption(false);
    }
  };

  const handleFixConsumption = async () => {
    if (!window.confirm(
      `Rewrite consumption_by_session for ${consumptionResult?.total_mismatched || 0} row(s) to match pick-slip required_qty?\n\nRows where the session already declared leftover (physically reconciled) will be skipped.`
    )) return;
    setIsFixingConsumption(true);
    try {
      const res = await apiClient.post('/wms/queue-admin/bundle-consumption-mismatch/fix');
      setConsumptionFixResult(res.data);
      toast.success(`Fixed ${res.data.fixed} consumption row(s)`);
      await handleScanConsumption();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fix failed');
    } finally {
      setIsFixingConsumption(false);
    }
  };

  const handleScanComboMismatch = async () => {
    setIsScanningComboMismatch(true);
    setComboMismatchFixResult(null);
    try {
      const res = await apiClient.get('/wms/queue-admin/combo-component-picked-mismatch');
      setComboMismatchResult(res.data);
      if (res.data.total_mismatched === 0) {
        toast.success('No inflated combo component picked_bundles found');
      } else {
        toast.info(`${res.data.total_mismatched} combo components have inflated counts`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setIsScanningComboMismatch(false);
    }
  };

  const handleFixComboMismatch = async () => {
    if (!window.confirm(`Rewrite picked_bundles for ${comboMismatchResult?.total_mismatched || 0} combo component(s) to match true picked+skipped count?`)) return;
    setIsFixingComboMismatch(true);
    try {
      const res = await apiClient.post('/wms/queue-admin/combo-component-picked-mismatch/fix');
      setComboMismatchFixResult(res.data);
      toast.success(`Fixed ${res.data.fixed} combo component(s)`);
      await handleScanComboMismatch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fix failed');
    } finally {
      setIsFixingComboMismatch(false);
    }
  };

  const handleScanStuckSlips = async () => {
    setIsLoadingStuck(true);
    try {
      const params = new URLSearchParams({
        min_hours: String(stuckMinHours || 12),
        zero_picked_only: String(stuckZeroOnly),
      });
      const res = await apiClient.get(`/wms/queue-admin/stuck-pick-slips?${params.toString()}`);
      setStuckResult(res.data);
      if (res.data.total_stuck === 0) {
        toast.success('No stuck pick slips found');
      } else {
        toast.info(`${res.data.total_stuck} stuck slips found (${res.data.regular_count} regular + ${res.data.combo_count} combo)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to scan stuck slips');
    } finally {
      setIsLoadingStuck(false);
    }
  };

  // --- Heal SFG SKU Collision ---
  const handleLookupHealBundle = async () => {
    const code = healBundleCode.trim();
    if (!code) return;
    setHealResult(null);
    setHealPreview(null);
    try {
      const res = await apiClient.post('/admin/sfg-inventory/heal-sku-collision', {
        bundle_code: code,
        preview: true,
      });
      if (res.data?.row) {
        setHealPreview(res.data.row);
      } else {
        toast.error('No active wms_sfg_inventory row for that bundle.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lookup failed');
    }
  };

  const handleApplyHealSku = async () => {
    const code = healBundleCode.trim();
    const reason = healReason.trim();
    if (!code || !reason) return;
    if (!window.confirm(
      `Archive the current SFG row for ${code}?\n\n` +
      `This moves it to wms_sfg_inventory_archive. The next put-away scan of this ` +
      `barcode will create a fresh row with the incoming SKU.`
    )) return;
    setIsHealingSku(true);
    setHealResult(null);
    try {
      const res = await apiClient.post('/admin/sfg-inventory/heal-sku-collision', {
        bundle_code: code,
        reason,
      });
      setHealResult(res.data);
      setHealPreview(null);
      toast.success(`Archived ${code} — ${res.data.archived?.sku_code} / ${res.data.archived?.status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Heal failed');
    } finally {
      setIsHealingSku(false);
    }
  };

  const handleEstimateReprintHeal = async () => {
    try {
      const res = await apiClient.get('/admin/pick-slips/heal-historical-reprints/estimate');
      setReprintHealCandidateCount(res.data?.candidate_slip_count ?? 0);
      toast.info(`${res.data.candidate_slip_count} non-packed slips queued for scan`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Estimate failed');
    }
  };

  const runReprintHealChunks = async (dryRun) => {
    setIsHealingReprints(true);
    setReprintHealProgress({
      processed: 0, slips_rewritten: 0, usns_rewritten: 0,
      running: true, dry_run: dryRun, done: false, error: null,
    });
    setReprintHealLog([]);
    const CHUNK = 25;
    let afterSlipNumber = null;
    let totalProcessed = 0;
    let totalSlipsRewritten = 0;
    let totalUsnsRewritten = 0;
    const logBuf = [];
    try {
      // Estimate first (for progress bar denominator)
      let denom = reprintHealCandidateCount;
      if (denom == null) {
        const est = await apiClient.get('/admin/pick-slips/heal-historical-reprints/estimate');
        denom = est.data.candidate_slip_count || 0;
        setReprintHealCandidateCount(denom);
      }
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await apiClient.post('/admin/pick-slips/heal-historical-reprints/scan', {
          limit: CHUNK,
          after_slip_number: afterSlipNumber,
          dry_run: dryRun,
        });
        const d = res.data;
        totalProcessed += d.processed || 0;
        totalSlipsRewritten += d.slips_rewritten || 0;
        totalUsnsRewritten += d.usns_rewritten || 0;
        if (d.details?.length) {
          for (const ent of d.details) {
            logBuf.push({
              slip: ent.pick_slip_number,
              rewrites: ent.rewrites,
            });
          }
          setReprintHealLog([...logBuf]);
        }
        setReprintHealProgress({
          processed: totalProcessed,
          slips_rewritten: totalSlipsRewritten,
          usns_rewritten: totalUsnsRewritten,
          running: true,
          dry_run: dryRun,
          done: false,
          error: null,
        });
        if (d.exhausted || !d.next_after) break;
        afterSlipNumber = d.next_after;
        // yield to UI
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 50));
      }
      setReprintHealProgress((p) => ({ ...p, running: false, done: true }));
      toast.success(
        dryRun
          ? `Dry run complete — ${totalSlipsRewritten} slip(s) / ${totalUsnsRewritten} USN(s) would be healed`
          : `Heal complete — ${totalSlipsRewritten} slip(s) / ${totalUsnsRewritten} USN(s) rewritten`,
      );
    } catch (e) {
      const msg = e.response?.data?.detail || 'Heal failed';
      setReprintHealProgress((p) => ({ ...p, running: false, error: msg }));
      toast.error(msg);
    } finally {
      setIsHealingReprints(false);
    }
  };


  const handleReleaseStuckSlip = async (slip, newStatus = 'PENDING') => {
    if (!window.confirm(
      `Release ${slip.slip_no} (${slip.slip_type}) from ${slip.picker_id}?\n\n` +
      `It will be set to ${newStatus} and can be picked up by any available picker.`
    )) return;
    setReleasingSlipNo(slip.slip_no);
    try {
      const params = new URLSearchParams({
        slip_no: slip.slip_no,
        slip_type: slip.slip_type,
        new_status: newStatus,
        reason: 'admin_force_release',
      });
      const res = await apiClient.post(`/wms/queue-admin/release-stuck-pick-slip?${params.toString()}`);
      toast.success(`${slip.slip_no} released → ${res.data.new_status}`);
      await handleScanStuckSlips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Release failed');
    } finally {
      setReleasingSlipNo(null);
    }
  };

  const handleScanOrphanCombos = async () => {
    setIsLoadingOrphans(true);
    setBulkOrphanResult(null);
    try {
      const res = await apiClient.get('/wms/queue-admin/orphan-combo-slips');
      setOrphanResult(res.data);
      if (res.data.total_orphans === 0) {
        toast.success('No orphan combo pick slips found');
      } else {
        toast.info(`${res.data.total_orphans} orphan combo slips found`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to scan orphan combo slips');
    } finally {
      setIsLoadingOrphans(false);
    }
  };

  const handleReconcileOrphan = async (slipNo, action) => {
    setReconcilingOrphanSlipNo(slipNo);
    try {
      const params = new URLSearchParams({
        slip_no: slipNo,
        action,
        reason: 'orphan_combo_reconcile',
      });
      const res = await apiClient.post(`/wms/queue-admin/reconcile-orphan-combo-slip?${params.toString()}`);
      toast.success(`${slipNo} → ${res.data.new_status}`);
      await handleScanOrphanCombos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconcilingOrphanSlipNo(null);
    }
  };

  const handleBulkReconcileOrphans = async (action = 'mirror_parent') => {
    const confirmMsg = action === 'cancel'
      ? `Cancel ALL ${orphanResult?.total_orphans || 0} orphan combo pick slips?`
      : `Mirror parent status for ALL ${orphanResult?.total_orphans || 0} orphan combo pick slips?\n(Finalized ones get COMPLETED/CANCELLED based on their parent group.)`;
    if (!window.confirm(confirmMsg)) return;
    setIsBulkReconcilingOrphans(true);
    try {
      const params = new URLSearchParams({
        action,
        reason: 'orphan_combo_reconcile_bulk',
      });
      const res = await apiClient.post(`/wms/queue-admin/reconcile-orphan-combo-slips-bulk?${params.toString()}`);
      setBulkOrphanResult(res.data);
      toast.success(`Reconciled: ${res.data.cancelled} cancelled, ${res.data.mirrored_completed} mirrored`);
      await handleScanOrphanCombos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk reconcile failed');
    } finally {
      setIsBulkReconcilingOrphans(false);
    }
  };

  const handleReconcileWipAssignments = async () => {
    setIsReconcilingWip(true);
    setWipReconcileResult(null);
    try {
      const response = await apiClient.post('/wms/dispatch/reconcile-wip-assignments');
      setWipReconcileResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'WIP reconciliation failed');
    } finally {
      setIsReconcilingWip(false);
    }
  };

  // 2026-05-14 — Ensure Re-Sticker perf indexes across all site DBs.
  // Backstop for the auto-create on startup; covers operators on
  // mwwms.com with multi-site DBs (sites_registry-driven).
  const handleEnsureResickerIndexes = async () => {
    setIsEnsuringIndexes(true);
    setEnsureIndexesResult(null);
    try {
      const res = await apiClient.post('/usn-replacement/admin/ensure-resticker-perf-indexes');
      setEnsureIndexesResult(res.data);
      toast.success(res.data?.summary || `Indexes verified across ${res.data?.sites_covered || 0} site DB(s)`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || String(e);
      setEnsureIndexesResult({ error: msg });
      toast.error(`Index ensure failed: ${msg}`);
    } finally {
      setIsEnsuringIndexes(false);
    }
  };




  const handleDedupeGreigeOps = async (dryRun = true) => {
    setIsDedupingGreige(true);
    if (dryRun) setGreigeDedupeResult(null);
    try {
      const res = await apiClient.post('/sections/greige/admin/dedupe-operations', { dry_run: dryRun });
      setGreigeDedupeResult(res.data);
      if (dryRun) {
        const n = res.data?.operation_entries_to_delete || 0;
        if (n === 0) toast.success('No duplicate greige operations found 🎉');
        else toast.info(`${n} duplicate operation row(s) across ${res.data?.groups_with_duplicates} bundle group(s) would be removed`);
      } else {
        toast.success(`Cleaned ${res.data?.operation_entries_deleted || 0} duplicate operation row(s) + ${res.data?.staff_work_rows_deleted || 0} wage row(s). Audit: ${res.data?.audit_id}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || String(e);
      toast.error(`Greige dedupe failed: ${msg}`);
    } finally {
      setIsDedupingGreige(false);
    }
  };

  const handleHealInhouseStatus = async (dryRun = true) => {
    setIsHealingInhouse(true);
    if (dryRun) setInhouseHealResult(null);
    try {
      const res = await apiClient.post('/wms/admin/heal-inhouse-status-on-sfg', { dry_run: dryRun });
      setInhouseHealResult(res.data);
      if (dryRun) {
        const n = res.data?.converted_to_transferred || 0;
        if (res.data?.scanned === 0) toast.success('No Inhouse-op statuses found on SFG inventory 🎉');
        else toast.info(`${n} SFG row(s) would be set to TRANSFERRED, ${res.data?.flagged_for_review || 0} flagged for review`);
      } else {
        toast.success(`Converted ${res.data?.converted_to_transferred || 0} → TRANSFERRED, flagged ${res.data?.flagged_for_review || 0} for review`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || String(e);
      toast.error(`Inhouse status heal failed: ${msg}`);
    } finally {
      setIsHealingInhouse(false);
    }
  };


  const handleHealSfgSku = async (dryRun = true) => {
    setIsHealingSfgSku(true);
    if (dryRun) setSfgSkuHealResult(null);
    try {
      const res = await apiClient.post('/wms/admin/heal-sfg-blank-sku', { dry_run: dryRun, limit: 50000 });
      setSfgSkuHealResult(res.data);
      if (dryRun) {
        if ((res.data?.total_blank || 0) === 0) toast.success('No blank SKU codes on SFG inventory 🎉');
        else toast.info(`${(res.data?.resolvable || 0).toLocaleString()} of ${(res.data?.total_blank || 0).toLocaleString()} blank rows can be resolved from origin`);
      } else {
        toast.success(`Backfilled ${(res.data?.updated || 0).toLocaleString()} SKU code(s) from origin`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || String(e);
      toast.error(`SFG SKU heal failed: ${msg}`);
    } finally {
      setIsHealingSfgSku(false);
    }
  };


  const handleRefreshInventoryDetails = async (dryRun = true) => {
    setIsRefreshingInventory(true);
    setInventoryRefreshResult(null);
    try {
      // Start the task
      await apiClient.post('/wms/fg/put-away/refresh-inventory-item-details', { dry_run: dryRun });
      toast.info(`Refresh started (${dryRun ? 'preview' : 'applying corrections'})...`);
      
      // Poll for status
      const poll = setInterval(async () => {
        try {
          const statusRes = await apiClient.get('/wms/fg/put-away/refresh-inventory-item-details/status');
          const status = statusRes.data;
          setInventoryRefreshResult(status);
          
          if (!status.running) {
            clearInterval(poll);
            setIsRefreshingInventory(false);
            if (status.error) {
              toast.error(`Refresh failed: ${status.error}`);
            } else if (status.dry_run) {
              toast.info(`Preview complete: ${status.total_corrected?.toLocaleString()} records would be corrected`);
            } else {
              toast.success(`Corrected ${status.total_corrected?.toLocaleString()} records`);
            }
          }
        } catch {
          clearInterval(poll);
          setIsRefreshingInventory(false);
        }
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Inventory refresh failed');
      setIsRefreshingInventory(false);
    }
  };


  // Backfill pack_barcode from usn_code — repairs legacy FG inventory rows
  // that surface with blank Pack/USN cells in the Re-Sticker Queue.
  const handleBackfillPackBarcode = async (dryRun = true) => {
    setIsBackfillingPackBarcode(true);
    setBackfillPackBarcodeResult(null);
    try {
      const response = await apiClient.post(
        '/price-revision/admin/backfill-pack-barcode',
        { dry_run: dryRun },
      );
      // Backend may return a 200 with `error` populated (e.g. slow-query
      // timeout, missing index). Treat that the same as a thrown error
      // so the operator sees the real cause instead of a generic message.
      if (response.data?.error) {
        setBackfillPackBarcodeResult({ error: response.data.error });
        toast.error(response.data.error);
        return;
      }
      setBackfillPackBarcodeResult(response.data);
      if (dryRun) {
        if (response.data.viable_rows === 0) {
          toast.info('Preview: no rows need backfilling — collection is already clean');
        } else {
          toast.info(`Preview: ${response.data.viable_rows.toLocaleString()} row(s) would be flipped`);
        }
      } else {
        toast.success(`Flipped ${response.data.updated.toLocaleString()} row(s). Refresh the Re-Sticker Queue to verify.`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Backfill failed';
      setBackfillPackBarcodeResult({ error: detail });
      toast.error(detail);
    } finally {
      setIsBackfillingPackBarcode(false);
    }
  };


  // Heal stuck PICKED_FOR_COMBO_V2 bundles — clears the "Picked for Combo"
  // label on SFG bundles whose owning Combo V2 plan/slip is already CANCELLED.
  const handleHealV2StuckBundles = async (dryRun = true) => {
    setIsHealingV2Bundles(true);
    setHealV2BundlesResult(null);
    try {
      const response = await apiClient.post(
        '/combo-v2/admin/heal-stuck-picked-bundles',
        { dry_run: dryRun },
      );
      // Backend may return a 200 with `error` populated (slow-query timeout,
      // missing index). Show that to the operator instead of a generic msg.
      if (response.data?.error) {
        setHealV2BundlesResult({ error: response.data.error });
        toast.error(response.data.error);
        return;
      }
      setHealV2BundlesResult(response.data);
      if (dryRun) {
        if (response.data.stuck_bundles_found === 0) {
          toast.info('Preview: no stuck PICKED_FOR_COMBO_V2 bundles — collection is clean');
        } else {
          toast.info(`Preview: ${response.data.stuck_bundles_found.toLocaleString()} bundle(s) would be released`);
        }
      } else {
        toast.success(`Released ${response.data.updated.toLocaleString()} bundle(s) back to STORED.`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Heal failed';
      setHealV2BundlesResult({ error: detail });
      toast.error(detail);
    } finally {
      setIsHealingV2Bundles(false);
    }
  };


  // Heal phantom FG inventory rows where USN has been deactivated by a
  // replacement. See state-declaration comment above for the why.
  const handleHealPhantomReplacedStock = async (dryRun = true) => {
    setIsHealingPhantom(true);
    setHealPhantomResult(null);
    try {
      const { data } = await apiClient.post(
        `/wms/admin/heal-phantom-replaced-stock?dry_run=${dryRun}`,
      );
      setHealPhantomResult(data);
      const summary = data?.summary || {};
      const total = (summary.renamed_in_place || 0) + (summary.voided || 0);
      if (dryRun) {
        if (total === 0) {
          toast.info('Preview: no phantom STORED rows found — inventory is clean.');
        } else {
          toast.info(
            `Preview: ${summary.renamed_in_place.toLocaleString()} row(s) to rename + `
            + `${summary.voided.toLocaleString()} to void.`,
          );
        }
      } else {
        toast.success(
          `Healed ${total.toLocaleString()} phantom row(s) (${summary.renamed_in_place.toLocaleString()} renamed, `
          + `${summary.voided.toLocaleString()} voided).`,
        );
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Heal failed';
      setHealPhantomResult({ error: detail });
      toast.error(detail);
    } finally {
      setIsHealingPhantom(false);
    }
  };

  const handleHealPhantomCSV = async (dryRun = true) => {
    try {
      const resp = await apiClient.get(
        `/wms/admin/heal-phantom-replaced-stock/export.csv?dry_run=${dryRun}`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `heal_phantom_replaced_stock_${dryRun ? 'preview' : 'committed'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CSV download failed');
    }
  };


  const handleFixDispatchReceipts = async (dryRun = true) => {
    setIsFixingDispatchReceipts(true);
    setDispatchReceiptResult(null);
    try {
      const response = await apiClient.post(`/internal-transfer/fix-missing-dispatch-receipts?dry_run=${dryRun}`);
      setDispatchReceiptResult(response.data);
      if (response.data.missing_count === 0) {
        toast.success('All bundles already have dispatch receipt records');
      } else if (dryRun) {
        toast.info(`Found ${response.data.missing_count} bundles missing dispatch receipts`);
      } else {
        toast.success(`Fixed ${response.data.fixed_count} missing dispatch receipt records`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dispatch receipt fix failed');
      setDispatchReceiptResult({ error: error.response?.data?.detail || 'Failed' });
    } finally {
      setIsFixingDispatchReceipts(false);
    }
  };




  const handleCleanupGhostWip = async (dryRun = true) => {
    setIsCleaningGhosts(true);
    if (dryRun) setGhostCleanupResult(null);
    try {
      const response = await apiClient.get(`/wms/dispatch/cleanup-ghost-wip-bundles?dry_run=${dryRun}`);
      setGhostCleanupResult(response.data);
      if (response.data.ghosts_found === 0) {
        toast.success('No ghost WIP bundles found');
      } else if (dryRun) {
        toast.info(`Found ${response.data.ghosts_found} ghost bundles out of ${response.data.total_wip} IN_WIP`);
      } else {
        toast.success(`Fixed ${response.data.fixed} ghost bundles`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ghost WIP cleanup failed');
      setGhostCleanupResult({ error: error.response?.data?.detail || 'Failed' });
    } finally {
      setIsCleaningGhosts(false);
    }
  };



  const handleRepairCompletedSlips = async (dryRun = true) => {
    setIsRepairing(true);
    if (dryRun) setRepairResult(null);
    try {
      const response = await apiClient.post('/wms/fg/put-away/repair-completed-slips', { dry_run: dryRun });
      setRepairResult(response.data);
      if (dryRun) {
        toast.info(`Preview: ${response.data.affected_slips} slips affected`);
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Repair failed');
    } finally {
      setIsRepairing(false);
    }
  };

  const handleDiagnoseSlip = async () => {
    if (!diagnoseSlipId.trim()) {
      toast.error('Enter a slip ID');
      return;
    }
    setIsDiagnosing(true);
    setDiagnosisResult(null);
    try {
      const response = await apiClient.get(`/wms/fg/put-away/diagnose-slip/${diagnoseSlipId.trim()}`);
      setDiagnosisResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Diagnosis failed');
    } finally {
      setIsDiagnosing(false);
    }
  };

  const handleRepairSpecificSlip = async (slipId, dryRun = true) => {
    setIsRepairing(true);
    try {
      const response = await apiClient.post('/wms/fg/put-away/repair-completed-slips', { 
        dry_run: dryRun, 
        slip_id: slipId 
      });
      setRepairResult(response.data);
      if (dryRun) {
        toast.info(`Preview: ${response.data.affected_slips} slips affected`);
      } else {
        toast.success(response.data.message);
        // Re-diagnose to see updated state
        handleDiagnoseSlip();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Repair failed');
    } finally {
      setIsRepairing(false);
    }
  };

  const handleForceDowngrade = async (slipId, dryRun = true) => {
    setIsDowngrading(true);
    if (dryRun) setDowngradeResult(null);
    try {
      const response = await apiClient.post(`/wms/fg/put-away/force-downgrade-slip/${slipId}`, { dry_run: dryRun });
      setDowngradeResult(response.data);
      if (dryRun) {
        toast.info(`Preview: ${response.data.items_downgraded} items would be downgraded, ${response.data.usns_removed_from_placed} USNs removed`);
      } else {
        toast.success(response.data.message);
        // Re-diagnose to see updated state
        handleDiagnoseSlip();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Force downgrade failed');
    } finally {
      setIsDowngrading(false);
    }
  };

  const handleRecreateIndexes = async () => {
    setIsRecreatingIndexes(true);
    try {
      const response = await apiClient.post('/backup/recreate-indexes');
      const jobId = response.data.job_id;
      toast.info('Index recreation started...');
      
      const poll = setInterval(async () => {
        try {
          const statusRes = await apiClient.get(`/backup/job/${jobId}`);
          if (statusRes.data.status === 'completed') {
            clearInterval(poll);
            setIsRecreatingIndexes(false);
            toast.success(statusRes.data.message || 'All indexes recreated successfully');
          } else if (statusRes.data.status === 'failed') {
            clearInterval(poll);
            setIsRecreatingIndexes(false);
            toast.error(statusRes.data.error || 'Index recreation failed');
          }
        } catch (e) {
          console.error('Error polling index job:', e);
        }
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start index recreation');
      setIsRecreatingIndexes(false);
    }
  };





  const handleReconcileWip = async (dryRun = true) => {
    setIsReconcilingWip(true);
    if (!dryRun) setWipReconcileResult(null);
    try {
      const response = await apiClient.post(`/wms/tools/reconcile-sfg-wip?dry_run=${dryRun}`);
      setWipReconcileResult(response.data);
      if (dryRun) {
        toast.info(`Found ${response.data.mismatches} mismatched bundles`);
      } else {
        toast.success(response.data.message || `Fixed ${response.data.fixed} bundles`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setIsReconcilingWip(false);
    }
  };

  // Scan Inhouse Bundle Receipts for usn_count vs len(usn_codes) mismatches
  const handleScanMismatchedBundles = async () => {
    setIsLoadingMismatched(true);
    setMismatchedApplyResult(null);
    try {
      const response = await apiClient.get('/processing-inward/mismatched-bundles');
      setMismatchedResult(response.data);
      if ((response.data?.total_bundles || 0) === 0) {
        toast.success('No mismatched bundles — all clean!');
      } else {
        toast.info(`Found ${response.data.total_bundles} bundle(s) with ${response.data.total_missing_usns} missing USN(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Scan failed');
    } finally {
      setIsLoadingMismatched(false);
    }
  };

  const handleTopUpOneBundle = async (bundleCode) => {
    try {
      const res = await apiClient.post('/processing-inward/top-up-usns', { bundle_code: bundleCode });
      toast.success(`${bundleCode}: topped up ${res.data.topped_up} USN(s)`);
      await handleScanMismatchedBundles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Top-up failed');
    }
  };

  const handleTopUpAllMismatched = async () => {
    if (!window.confirm('Top-up ALL missing USNs across all mismatched bundles? New PI USNs will be claimed from the pool.')) {
      return;
    }
    setIsToppingUpMismatched(true);
    setMismatchedApplyResult(null);
    try {
      const res = await apiClient.post('/processing-inward/reconcile-mismatched-bundles?dry_run=false');
      setMismatchedApplyResult(res.data);
      toast.success(`Topped up ${res.data.total_topped_up} USN(s) across ${res.data.total_bundles} bundle(s)`);
      await handleScanMismatchedBundles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk top-up failed');
    } finally {
      setIsToppingUpMismatched(false);
    }
  };

  // GS Children Metadata Backfill — legacy children without item_index / source_bundle_barcode



  const handleResetAssignedSlips = async () => {
    if (!window.confirm('This will reset ALL assigned SFG pick slips back to the priority queue. Continue?')) return;
    setIsResettingAssigned(true);
    setResetAssignedResult(null);
    try {
      const response = await apiClient.post('/wms/tools/reset-assigned-slips');
      setResetAssignedResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset assigned slips');
    } finally {
      setIsResettingAssigned(false);
    }
  };

  // Release stuck bundles from cancelled slips
  const [isReleasingStuck, setIsReleasingStuck] = useState(false);
  const [stuckBundleResult, setStuckBundleResult] = useState(null);

  // Cleanup user roles
  const [isCleaningRoles, setIsCleaningRoles] = useState(false);
  const [cleanRolesResult, setCleanRolesResult] = useState(null);

  // Fix cleared WIP bundles

  // Release orphaned bundles (missing slips)
  const [isReleasingOrphaned, setIsReleasingOrphaned] = useState(false);
  const [orphanedResult, setOrphanedResult] = useState(null);
  // USN double-claim audit / heal (added 2026-05-25 — surfaces
  // USNs that label-printers claimed despite being operationally
  // consumed in packing/combo/DC. See routes/admin.py).
  const [isAuditingDC, setIsAuditingDC] = useState(false);
  const [dcAuditResult, setDcAuditResult] = useState(null);
  const [isHealingDCBulk, setIsHealingDCBulk] = useState(false);
  const [dcHealResult, setDcHealResult] = useState(null);
  const [singleDcUsnInput, setSingleDcUsnInput] = useState('');
  const [isHealingSingleDC, setIsHealingSingleDC] = useState(false);
  const [singleDcResult, setSingleDcResult] = useState(null);
  const [isDetectingDuplicates, setIsDetectingDuplicates] = useState(false);
  const [duplicateSlipResult, setDuplicateSlipResult] = useState(null);

  // 2026-05-26 — Bulk Reprint Contaminated USNs section state.
  // Separate from `dcAuditResult` because that list is built for FRESH
  // contamination detection (status='used_for_label'). Already-healed
  // USNs (status='contaminated_double_claim') are what the user needs
  // for the recovery sweep — fetched via include_resolved=true.
  const [bulkReprintSelected, setBulkReprintSelected] = useState(new Set());
  const [bulkReprintPage, setBulkReprintPage] = useState(0);
  const [bulkReprintBatchSize, setBulkReprintBatchSize] = useState(50);
  const [pendingReprintList, setPendingReprintList] = useState(null);
  const [isLoadingPendingReprint, setIsLoadingPendingReprint] = useState(false);

  const handleLoadPendingReprintList = async () => {
    setIsLoadingPendingReprint(true);
    try {
      const response = await apiClient.get(
        '/admin/audit-double-claimed-usns?limit=5000&include_resolved=true',
      );
      // Filter to ONLY already-healed (detection_method='blocked_by_guard')
      // so the table is purely the pending-reprint queue, not active leaks.
      const healed = (response.data.contaminated || []).filter(
        r => r.detection_method === 'blocked_by_guard'
          || r.status === 'contaminated_double_claim'
      );
      setPendingReprintList({
        ...response.data,
        contaminated: healed,
        healed_count: healed.length,
      });
      setBulkReprintSelected(new Set());
      setBulkReprintPage(0);
      if (healed.length === 0) {
        toast.info('No healed USNs awaiting reprint. Run audit + heal first.');
      } else {
        toast.success(`Loaded ${healed.length} contaminated USN(s) awaiting fresh codes.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load pending reprint list');
    } finally {
      setIsLoadingPendingReprint(false);
    }
  };

  // Fix stuck combo label sessions
  const [isFixingComboLabels, setIsFixingComboLabels] = useState(false);
  const [comboLabelFixResult, setComboLabelFixResult] = useState(null);
  const [forceFixingId, setForceFixingId] = useState(null);

  const handleReleaseStuckBundles = async (dryRun) => {
    if (!dryRun && !window.confirm('This will release ALL stuck bundles from cancelled slips back to STORED. Continue?')) return;
    setIsReleasingStuck(true);
    if (!dryRun) setStuckBundleResult(null);
    try {
      const response = await apiClient.post(`/wms/tools/release-cancelled-slip-bundles?dry_run=${dryRun}`);
      setStuckBundleResult(response.data);
      if (!dryRun) toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release stuck bundles');
    } finally {
      setIsReleasingStuck(false);
    }
  };

  const handleCleanupRoles = async (dryRun) => {
    if (!dryRun && !window.confirm('This will permanently delete all invalid roles. Continue?')) return;
    setIsCleaningRoles(true);
    if (!dryRun) setCleanRolesResult(null);
    try {
      const response = await apiClient.post(`/wms/tools/cleanup-user-roles?dry_run=${dryRun}`);
      setCleanRolesResult(response.data);
      if (!dryRun) toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup roles');
    } finally {
      setIsCleaningRoles(false);
    }
  };


  const handleReleaseOrphaned = async (dryRun) => {
    if (!dryRun && !window.confirm('This will release all orphaned bundles (missing slips) back to STORED. Continue?')) return;
    setIsReleasingOrphaned(true);
    if (!dryRun) setOrphanedResult(null);
    try {
      const response = await apiClient.post(`/wms/tools/release-orphaned-bundles?dry_run=${dryRun}`);
      setOrphanedResult(response.data);
      if (!dryRun) toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release orphaned bundles');
    } finally {
      setIsReleasingOrphaned(false);
    }
  };

  // ===== USN Double-Claim Tools (2026-05-25) =====
  // 1) Audit — read-only scan; lists USNs whose label claim
  //    overlaps an operational consumption (picking/packing/etc).
  const handleAuditDoubleClaim = async () => {
    setIsAuditingDC(true);
    setDcAuditResult(null);
    try {
      const response = await apiClient.get('/admin/audit-double-claimed-usns?limit=2000');
      setDcAuditResult(response.data);
      if (response.data.contaminated_found > 0) {
        toast.warning(`Found ${response.data.contaminated_found} contaminated USN(s)`);
      } else {
        toast.success('No contaminated USNs found — system clean.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Audit failed');
    } finally {
      setIsAuditingDC(false);
    }
  };

  // 2) Bulk heal — reverts every contaminated USN to
  //    `contaminated_double_claim` so it can never be re-claimed.
  const handleHealDoubleClaimBulk = async (dryRun) => {
    if (!dryRun && !window.confirm(
      'This will revert every contaminated USN to "contaminated_double_claim". ' +
      'The associated label sessions must still be cancelled manually. Continue?'
    )) return;
    setIsHealingDCBulk(true);
    if (!dryRun) setDcHealResult(null);
    try {
      const response = await apiClient.post(`/admin/heal-double-claimed-usns-bulk?dry_run=${dryRun}`);
      setDcHealResult(response.data);
      if (!dryRun) toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Heal failed');
    } finally {
      setIsHealingDCBulk(false);
    }
  };

  // 3) Single USN heal — for targeted reverts of a specific code.
  const handleHealDoubleClaimSingle = async (dryRun) => {
    const code = (singleDcUsnInput || '').trim();
    if (!code) {
      toast.error('Enter a USN code first');
      return;
    }
    if (!dryRun && !window.confirm(`Revert USN ${code} to 'contaminated_double_claim'?`)) return;
    setIsHealingSingleDC(true);
    if (!dryRun) setSingleDcResult(null);
    try {
      const response = await apiClient.post(
        `/admin/heal-double-claimed-usn?usn_code=${encodeURIComponent(code)}&dry_run=${dryRun}`
      );
      setSingleDcResult(response.data);
      if (!dryRun) toast.success(`Reverted ${code}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Heal failed');
    } finally {
      setIsHealingSingleDC(false);
    }
  };
  // ===== end USN Double-Claim Tools =====

  // ===== Bulk Reprint helpers (2026-05-26) =====
  // Source of rows = pending-reprint list (already-healed USNs) if loaded,
  // otherwise the live audit list (fresh contamination). Operator drives
  // which by clicking the dedicated button.
  const bulkReprintRows = (pendingReprintList?.contaminated || dcAuditResult?.contaminated || []);
  const bulkSourceLabel = pendingReprintList ? 'pending-reprint queue' : 'live audit';
  const bulkPageStart = bulkReprintPage * bulkReprintBatchSize;
  const bulkPageEnd = bulkPageStart + bulkReprintBatchSize;
  const bulkVisibleRows = bulkReprintRows.slice(bulkPageStart, bulkPageEnd);
  const bulkTotalPages = Math.max(1, Math.ceil(bulkReprintRows.length / bulkReprintBatchSize));

  const toggleBulkSelected = (usn) => {
    setBulkReprintSelected(prev => {
      const next = new Set(prev);
      if (next.has(usn)) next.delete(usn); else next.add(usn);
      return next;
    });
  };
  const selectAllVisible = () => {
    setBulkReprintSelected(prev => {
      const next = new Set(prev);
      bulkVisibleRows.forEach(r => next.add(r.usn_code));
      return next;
    });
  };
  const selectAllContaminated = () => {
    setBulkReprintSelected(new Set(bulkReprintRows.map(r => r.usn_code)));
  };
  const clearBulkSelection = () => setBulkReprintSelected(new Set());

  const handleSendToReprint = () => {
    if (bulkReprintSelected.size === 0) {
      toast.error('Select at least one USN to reprint');
      return;
    }
    // Build the preset batch. Combo USNs (CMB- prefix) get a synthetic
    // pair_number starting at 1, incrementing only across COMBO rows so
    // operators see contiguous "Pair 1, 2, 3..." in the queue. Non-combo
    // pieces keep pair_number = null.
    let pairCursor = 0;
    const presetBatch = [];
    for (const r of bulkReprintRows) {
      if (!bulkReprintSelected.has(r.usn_code)) continue;
      const isCombo = String(r.usn_code || '').toUpperCase().startsWith('CMB-')
        || /combo/i.test(r.operationally_consumed_in || '');
      if (isCombo) pairCursor += 1;
      presetBatch.push({
        usn_code: r.usn_code,
        pair_number: isCombo ? pairCursor : null,
        is_combo: isCombo,
        is_contaminated: true,
        operationally_consumed_in: r.operationally_consumed_in || '',
      });
    }
    toast.success(`${presetBatch.length} USN(s) queued — connect printer & click Print Batch.`);
    navigate('/wms/fg/usn-label-reprint', {
      state: {
        presetBatch,
        sourceFlow: 'contaminated_bulk_reprint',
      },
    });
  };
  // ===== end Bulk Reprint helpers =====


  const handleDetectDuplicateSlips = async (dryRun) => {
    if (!dryRun && !window.confirm('This will cancel duplicate pick slips and mark their bundles as CONSUMED. Continue?')) return;
    setIsDetectingDuplicates(true);
    if (!dryRun) setDuplicateSlipResult(null);
    try {
      const response = await apiClient.post(`/wms/tools/detect-duplicate-pick-slips?dry_run=${dryRun}`);
      setDuplicateSlipResult(response.data);
      if (!dryRun) toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to detect duplicate slips');
    } finally {
      setIsDetectingDuplicates(false);
    }
  };

  const handleFixStuckComboLabels = async () => {
    setIsFixingComboLabels(true);
    setComboLabelFixResult(null);
    try {
      const response = await apiClient.post('/combo/assembly/tools/fix-stuck-label-sessions');
      setComboLabelFixResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix stuck sessions');
    } finally {
      setIsFixingComboLabels(false);
    }
  };

  const handleForceFixSession = async (sessionId) => {
    if (!window.confirm(`This will mark ALL labels as printed for session ${sessionId} and advance it to Return Excess. Only use if stickers were physically printed. Continue?`)) return;
    setForceFixingId(sessionId);
    try {
      const response = await apiClient.post('/combo/assembly/tools/force-fix-label-session', { session_id: sessionId });
      toast.success(response.data.message);
      // Re-run the scan to refresh results
      handleFixStuckComboLabels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to force-fix session');
    } finally {
      setForceFixingId(null);
    }
  };

  const handleForceFixAll = async () => {
    const stuckSessions = comboLabelFixResult?.still_stuck_sessions || [];
    if (!stuckSessions.length) return;
    if (!window.confirm(`This will force-fix ALL ${stuckSessions.length} stuck session(s), marking all their labels as printed. Only use if stickers were physically printed. Continue?`)) return;
    setForceFixingId('all');
    let fixedCount = 0;
    for (const s of stuckSessions) {
      try {
        await apiClient.post('/combo/assembly/tools/force-fix-label-session', { session_id: s.session_id });
        fixedCount++;
      } catch (error) {
        toast.error(`Failed to fix ${s.session_id}: ${error.response?.data?.detail || 'Error'}`);
      }
    }
    toast.success(`Force-fixed ${fixedCount}/${stuckSessions.length} sessions`);
    setForceFixingId(null);
    handleFixStuckComboLabels();
  };

  const getStatusBadge = (status) => {
    const configs = {
      'pending': { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
      'assigned': { label: 'Assigned', className: 'bg-blue-100 text-blue-800' },
      'in_progress': { label: 'In Progress', className: 'bg-purple-100 text-purple-800' },
      'completed': { label: 'Completed', className: 'bg-green-100 text-green-800' }
    };
    const config = configs[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${config.className}`}>{config.label}</span>;
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">Put Away Supervisor</h1>
                <p className="text-sm text-[#E8DCC8]">FG WMS - Manage put away operations</p>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => {
            fetchAvailableStaff();
            fetchSlips();
            fetchDashboardStats();
            fetchStaffStats();
          }} data-testid="refresh-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats Cards - Simplified for Table Clearance model */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="py-4 text-center">
              <p className="text-sm text-green-600">Completed Today</p>
              <p className="text-3xl font-bold text-green-700" data-testid="completed-count">{dashboardStats.slips.completed_today}</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="py-4 text-center">
              <p className="text-sm text-orange-600">Awaiting Put Away</p>
              <p className="text-3xl font-bold text-orange-700" data-testid="awaiting-count">{dashboardStats.pieces_awaiting_put_away}</p>
              <p className="text-xs text-orange-500">pieces</p>
            </CardContent>
          </Card>
          <Card className="bg-indigo-50 border-indigo-200">
            <CardContent className="py-4 text-center">
              <p className="text-sm text-indigo-600">GT Bundles Ready</p>
              <p className="text-3xl font-bold text-indigo-700" data-testid="gt-eligible-count">
                {dashboardStats.goods_transfer?.bundles_eligible || 0}
              </p>
              <p className="text-xs text-indigo-500">from Goods Transfer</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="py-4 text-center">
              <p className="text-sm text-purple-600">Available Staff</p>
              <p className="text-3xl font-bold text-purple-700" data-testid="staff-count">{dashboardStats.available_staff}</p>
            </CardContent>
          </Card>
        </div>

        {/* Legacy Slip Stats (collapsed, for reference) */}
        {(dashboardStats.slips.pending > 0 || dashboardStats.slips.assigned > 0 || dashboardStats.slips.in_progress > 0) && (
          <Card className="mb-6 border-amber-200 bg-amber-50/50">
            <CardContent className="py-3 px-4">
              <p className="text-xs text-amber-700 font-semibold mb-2">Legacy Slips (Read-Only)</p>
              <div className="flex gap-4 text-sm">
                <span className="text-amber-600">Pending: <strong>{dashboardStats.slips.pending}</strong></span>
                <span className="text-blue-600">Assigned: <strong>{dashboardStats.slips.assigned}</strong></span>
                <span className="text-purple-600">In Progress: <strong>{dashboardStats.slips.in_progress}</strong></span>
                {dashboardStats.slips.in_progress > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={openResetDialog}
                    className="text-xs text-purple-600 border-purple-300 hover:bg-purple-100 h-6 px-2"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Reset Stuck
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="staff" data-testid="tab-staff">Available Staff</TabsTrigger>
            <TabsTrigger value="stats" data-testid="tab-stats">Staff Stats</TabsTrigger>
            <TabsTrigger value="slips" data-testid="tab-slips">Legacy Slips</TabsTrigger>
            <TabsTrigger value="tools" data-testid="tab-tools" >
              <Wrench className="w-4 h-4 mr-1" /> Tools
            </TabsTrigger>
          </TabsList>

          {/* Staff Tab - Similar to Dispatch Supervisor */}
          <TabsContent value="staff">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-emerald-500" />
                  Put Away Staff
                </CardTitle>
                <CardDescription>Select staff available for put away operations today</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Search staff..."
                    value={staffSearch}
                    onChange={(e) => setStaffSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchStaff()}
                    data-testid="staff-search-input"
                  />
                  <Button variant="outline" onClick={handleSearchStaff} data-testid="search-button">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>

                {/* Staff list with checkboxes */}
                <div className="border rounded-lg max-h-64 overflow-y-auto" data-testid="staff-list">
                  {isLoadingStaff ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin" />
                    </div>
                  ) : staffList.length === 0 ? (
                    <p className="p-4 text-center text-muted-foreground">No staff found</p>
                  ) : (
                    staffList.map((staff) => (
                      <label
                        key={staff.staff_id}
                        className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                        data-testid={`staff-row-${staff.staff_id}`}
                      >
                        <Checkbox
                          checked={selectedStaffIds.includes(staff.staff_id)}
                          onCheckedChange={() => handleToggleStaff(staff.staff_id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{staff.staff_name || staff.name || 'N/A'}</p>
                          <p className="text-xs text-muted-foreground">{staff.staff_id}</p>
                        </div>
                        {selectedStaffIds.includes(staff.staff_id) && (
                          <Badge variant="outline" className="text-green-600 bg-green-50">Selected</Badge>
                        )}
                      </label>
                    ))
                  )}
                </div>

                {/* Selected count and save */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground" data-testid="selected-count">
                    {selectedStaffIds.length} staff selected
                  </span>
                  <Button onClick={handleSaveStaff} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="save-staff-button">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save Put Away Staff
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Today's Available Staff Summary */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Today's Available Staff ({availableStaff.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {availableStaff.map(staff => (
                    <Badge key={staff.staff_id} variant="outline" className="bg-emerald-50 text-emerald-700">
                      {staff.staff_name || staff.staff_id}
                    </Badge>
                  ))}
                  {availableStaff.length === 0 && (
                    <span className="text-muted-foreground text-sm">No staff selected for today</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Slips Tab - Legacy Read-Only */}
          <TabsContent value="slips">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Legacy Put Away Slips
                  <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">READ-ONLY</Badge>
                </CardTitle>
                <CardDescription>Historical slip data. New put-away uses Table Clearance flow.</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingSlips ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin" />
                  </div>
                ) : slips.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No put away slips found
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Slip ID</TableHead>
                          <TableHead>Pieces</TableHead>
                          <TableHead>Designs</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Assigned To</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {slips.map((slip) => (
                          <TableRow key={slip.slip_id} data-testid={`slip-row-${slip.slip_id}`} className={slip.auto_generated ? 'bg-emerald-50/50' : ''}>
                            <TableCell className="font-mono font-semibold">
                              <div className="flex items-center gap-2">
                                {slip.slip_id}
                                {slip.auto_generated && (
                                  <Badge variant="outline" className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300">
                                    <Zap className="w-3 h-3 mr-1" />
                                    Auto
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{slip.total_pieces} pcs</TableCell>
                            <TableCell>{slip.total_designs}</TableCell>
                            <TableCell>{getStatusBadge(slip.status)}</TableCell>
                            <TableCell>{slip.assigned_to_name || slip.assigned_to || '-'}</TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {new Date(slip.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setViewingSlip(slip);
                                    setSlipDetailOpen(true);
                                  }}
                                  data-testid={`view-button-${slip.slip_id}`}
                                >
                                  View
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <Card>
              <CardHeader>
                <CardTitle>Staff Performance - Today</CardTitle>
              </CardHeader>
              <CardContent>
                {staffStats.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No staff activity today</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Staff</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Assigned Slips</TableHead>
                          <TableHead>Completed</TableHead>
                          <TableHead>Total Pieces</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {staffStats.map((stat, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{stat.staff_name || stat.staff_id}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs ${
                                stat.status === 'available' ? 'bg-green-100 text-green-800' :
                                stat.status === 'busy' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {stat.status || 'available'}
                              </span>
                            </TableCell>
                            <TableCell>{stat.assigned_slips || 0}</TableCell>
                            <TableCell>{stat.completed_slips || 0}</TableCell>
                            <TableCell>{stat.total_pieces || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GT Slips Tab - Goods Transfer Put Away Slips (One per Bundle) */}
          <TabsContent value="gt-slips">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-500" />
                  GT Put Away Slips
                  <Badge variant="outline" className="ml-2">One per Bundle</Badge>
                </CardTitle>
                <CardDescription>
                  Goods Transfer Put Away slips - each slip represents one bundle with Bundle Number shown in "Find Pieces At"
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Stats Row */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="p-3 bg-yellow-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-yellow-700">{gtSlipsStats.pending}</p>
                    <p className="text-xs text-yellow-600">Pending</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-700">{gtSlipsStats.assigned}</p>
                    <p className="text-xs text-blue-600">Assigned</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-700">{gtSlipsStats.in_progress}</p>
                    <p className="text-xs text-orange-600">In Progress</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{gtSlipsStats.completed}</p>
                    <p className="text-xs text-green-600">Completed</p>
                  </div>
                </div>

                {/* Refresh Button */}
                <div className="flex justify-end mb-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => fetchGTSlips()}
                    disabled={isLoadingGTSlips}
                    data-testid="refresh-gt-slips"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingGTSlips ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                {/* GT Slips Table */}
                {isLoadingGTSlips ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin" />
                  </div>
                ) : gtSlips.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No GT Put Away slips yet. Click "Generate GT Put Away Slip" to create slips.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Slip ID</TableHead>
                          <TableHead>Bundle No</TableHead>
                          <TableHead>Design</TableHead>
                          <TableHead className="text-center">Pieces</TableHead>
                          <TableHead>Assigned To</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Created</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {gtSlips.map(slip => (
                          <TableRow 
                            key={slip.slip_id} 
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => { setViewingSlip(slip); setSlipDetailOpen(true); }}
                            data-testid={`gt-slip-row-${slip.slip_id}`}
                          >
                            <TableCell className="font-mono text-sm">{slip.slip_id}</TableCell>
                            <TableCell className="font-bold text-purple-700">
                              {slip.bundle_code || slip.source_bundles?.[0] || '-'}
                            </TableCell>
                            <TableCell>
                              {slip.items?.[0]?.design_name || 'Unknown'}
                              {slip.items?.[0]?.shade && <span className="text-xs text-gray-500 ml-1">({slip.items[0].shade})</span>}
                            </TableCell>
                            <TableCell className="text-center font-bold">{slip.total_pieces}</TableCell>
                            <TableCell>{slip.assigned_to_name || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={
                                slip.status === 'completed' ? 'success' :
                                slip.status === 'in_progress' ? 'warning' :
                                slip.status === 'assigned' ? 'secondary' : 'outline'
                              } className={
                                slip.status === 'completed' ? 'bg-green-100 text-green-700' :
                                slip.status === 'in_progress' ? 'bg-orange-100 text-orange-700' :
                                slip.status === 'assigned' ? 'bg-blue-100 text-blue-700' : ''
                              }>
                                {slip.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-gray-500">
                              {new Date(slip.created_at).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Skipped Items Tab */}
          <TabsContent value="skipped">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <SkipForward className="w-5 h-5 text-amber-600" />
                      Skipped Items Queue
                    </CardTitle>
                    <CardDescription>
                      Items skipped during put away - review and take action
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchSkippedItems}
                    disabled={isLoadingSkipped}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingSkipped ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                
                {/* Status Summary */}
                <div className="flex gap-4 mt-4">
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                    Pending Review: {skippedCounts.pending_review}
                  </Badge>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                    Reassigned: {skippedCounts.reassigned}
                  </Badge>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                    Returned to SFG: {skippedCounts.returned_to_sfg}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingSkipped ? (
                  <div className="text-center py-8 text-gray-500">Loading...</div>
                ) : skippedItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <SkipForward className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No skipped items pending review</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Skipped ID</TableHead>
                        <TableHead>Design / Item</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Skipped By</TableHead>
                        <TableHead>Original Slip</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {skippedItems.map((item) => (
                        <TableRow key={item.skipped_id} className="bg-amber-50/30">
                          <TableCell className="font-mono text-sm">{item.skipped_id}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.design_name}</p>
                              <p className="text-xs text-gray-500">{item.shade} · {item.size}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-bold text-amber-700">{item.skipped_qty} pcs</p>
                              {item.placed_qty > 0 && (
                                <p className="text-xs text-green-600">{item.placed_qty} placed</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                              {item.reason}
                            </Badge>
                            {item.reason_details && (
                              <p className="text-xs text-gray-500 mt-1">{item.reason_details}</p>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{item.skipped_by_name}</TableCell>
                          <TableCell className="font-mono text-sm">{item.original_slip_id}</TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {new Date(item.skipped_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2 justify-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedSkippedItem(item);
                                  setSkippedActionNotes('');
                                  setSkippedActionDialog('reassign');
                                }}
                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                title="Reassign to put away staff"
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Reassign
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedSkippedItem(item);
                                  setSkippedActionNotes('');
                                  setSkippedActionDialog('return_sfg');
                                }}
                                className="text-green-600 border-green-300 hover:bg-green-50"
                                title="Return to SFG Storage"
                              >
                                <Warehouse className="w-4 h-4 mr-1" />
                                To SFG
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tools">
          {/* Tools Tab */}

            {/* === Sub-section: ⚡ Performance Indexes (Re-Sticker Queue) === */}
            {/* 2026-05-14 — One-click index ensure to keep FG Re-Sticker
                Queue Print Batch fast across all site DBs. Auto-runs on
                backend startup; this button is the operator-visible
                backstop for multi-site coverage. */}
            <div className="mb-4 mt-2 first:mt-0">
              <div className="flex items-center justify-between border-b-2 border-slate-300 pb-2 mb-4">
                <h2 className="text-xl font-bold text-slate-800">⚡ Performance Indexes</h2>
                <span className="text-xs text-slate-500">Run after a fresh deploy if any site DB is missing the Re-Sticker multikey indexes.</span>
              </div>
            </div>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Ensure Re-Sticker Queue Indexes</CardTitle>
                <CardDescription>
                  Idempotently creates 3 perf-critical multikey indexes used by USN replacement (the engine behind every Re-Sticker batch):{' '}
                  <code className="text-[11px]">wms_fg_pick_slips.items.picked_packs.pack_barcode</code>,{' '}
                  <code className="text-[11px]">wms_pick_slips.items.picked_packs.pack_barcode</code>,{' '}
                  <code className="text-[11px]">dispatch_received_bundle_usns.usns.usn_code</code>.
                  Without these, large batches (15+ stickers) hit Mongo collection scans and time out. Auto-runs on backend startup; this button covers every site DB via{' '}
                  <code className="text-[11px]">sites_registry</code>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleEnsureResickerIndexes}
                  disabled={isEnsuringIndexes}
                  data-testid="ensure-resticker-indexes-btn"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isEnsuringIndexes ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Verifying indexes…</>
                  ) : (
                    <><Database className="w-4 h-4 mr-2" /> Ensure Re-Sticker Indexes</>
                  )}
                </Button>

                {ensureIndexesResult && (
                  <div
                    className={`border rounded-lg p-4 space-y-2 text-sm ${
                      ensureIndexesResult.error
                        ? 'bg-red-50 border-red-200'
                        : 'bg-green-50 border-green-200'
                    }`}
                    data-testid="ensure-resticker-indexes-result"
                  >
                    {ensureIndexesResult.error ? (
                      <div className="text-red-700">Error: {ensureIndexesResult.error}</div>
                    ) : (
                      <>
                        <div className="font-medium text-green-800">
                          ✓ {ensureIndexesResult.summary}
                        </div>
                        <div className="text-xs text-slate-600">
                          Sites covered: {ensureIndexesResult.sites_covered}
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {(ensureIndexesResult.results || []).map((s) => (
                            <div key={s.site_id} className="bg-white rounded p-2 border border-slate-200">
                              <div className="font-mono text-xs font-semibold text-slate-700 mb-1">{s.site_id}</div>
                              <ul className="text-[11px] space-y-0.5">
                                {(s.indexes || []).map((ix, i) => (
                                  <li key={i} className={ix.ok ? 'text-emerald-700' : 'text-red-700'}>
                                    {ix.ok ? '✓' : '✗'} <code>{ix.collection}.{ix.field}</code>
                                    {ix.index_name ? ` → ${ix.index_name}` : ''}
                                    {ix.error ? ` (${ix.error})` : ''}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* === Sub-section: 🧹 Data Cleanup === */}
            <div className="mb-4 mt-8 first:mt-0">
              <div className="flex items-center justify-between border-b-2 border-slate-300 pb-2 mb-4">
                <h2 className="text-xl font-bold text-slate-800">🧹 Data Cleanup</h2>
                <span className="text-xs text-slate-500">Remove duplicate scans caused by network failures.</span>
              </div>
            </div>

            <Card className="mb-6" data-testid="greige-dedupe-card">
              <CardHeader>
                <CardTitle className="text-lg">Clean Duplicate Greige Operations</CardTitle>
                <CardDescription>
                  Network failures during scanning can post the SAME operation (Sort No Assignment, Inspection,
                  Pinning, Packing, Picking, etc.) multiple times for the same bundle. This keeps the{' '}
                  <strong>earliest</strong> entry and removes the later duplicates from both{' '}
                  <code className="text-[11px]">greige_operations</code> and the wage rows in{' '}
                  <code className="text-[11px]">greige_staff_work</code> (so wages &amp; pieces-processed self-correct).
                  Always run <strong>Preview</strong> first — Apply snapshots every deleted row into an audit log.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleDedupeGreigeOps(true)}
                    disabled={isDedupingGreige}
                    data-testid="greige-dedupe-preview-btn"
                    variant="outline"
                  >
                    {isDedupingGreige ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
                    ) : (
                      <><Database className="w-4 h-4 mr-2" /> Preview Duplicates</>
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      if (window.confirm('Permanently delete the duplicate greige operation rows and their wage rows? The earliest entry of each group is kept. A restorable audit snapshot is saved.')) {
                        handleDedupeGreigeOps(false);
                      }
                    }}
                    disabled={isDedupingGreige || !greigeDedupeResult || (greigeDedupeResult?.operation_entries_to_delete || 0) === 0}
                    data-testid="greige-dedupe-apply-btn"
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Apply Cleanup
                  </Button>
                </div>

                {greigeDedupeResult && (
                  <div
                    className="border rounded-lg p-4 space-y-3 text-sm bg-slate-50 border-slate-200"
                    data-testid="greige-dedupe-result"
                  >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-white rounded p-2 border">
                        <div className="text-[11px] text-slate-500">Entries scanned</div>
                        <div className="text-lg font-bold">{(greigeDedupeResult.total_entries_scanned || 0).toLocaleString()}</div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-[11px] text-slate-500">Duplicate groups</div>
                        <div className="text-lg font-bold">{(greigeDedupeResult.groups_with_duplicates || 0).toLocaleString()}</div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-[11px] text-slate-500">{greigeDedupeResult.applied ? 'Ops deleted' : 'Ops to delete'}</div>
                        <div className="text-lg font-bold text-red-600">
                          {((greigeDedupeResult.applied ? greigeDedupeResult.operation_entries_deleted : greigeDedupeResult.operation_entries_to_delete) || 0).toLocaleString()}
                        </div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-[11px] text-slate-500">{greigeDedupeResult.applied ? 'Wage rows deleted' : 'Wage rows to delete'}</div>
                        <div className="text-lg font-bold text-red-600">
                          {((greigeDedupeResult.applied ? greigeDedupeResult.staff_work_rows_deleted : greigeDedupeResult.staff_work_rows_to_delete) || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {greigeDedupeResult.applied && (
                      <div className="text-emerald-700 font-medium" data-testid="greige-dedupe-applied-banner">
                        ✓ Cleanup applied. Audit ID: <code>{greigeDedupeResult.audit_id}</code>
                      </div>
                    )}

                    {greigeDedupeResult.per_operation && Object.keys(greigeDedupeResult.per_operation).length > 0 && (
                      <div>
                        <div className="font-medium text-slate-700 mb-1">By operation</div>
                        <ul className="text-xs space-y-0.5">
                          {Object.entries(greigeDedupeResult.per_operation).map(([op, v]) => (
                            <li key={op} className="text-slate-600">
                              <span className="font-mono">{op}</span> — {v.entries_to_delete} duplicate(s) across {v.duplicate_groups} group(s)
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(greigeDedupeResult.sample_groups || []).length > 0 && (
                      <div className="max-h-64 overflow-y-auto">
                        <div className="font-medium text-slate-700 mb-1">Sample duplicate groups</div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-slate-500 border-b">
                              <th className="py-1 pr-2">Operation</th>
                              <th className="py-1 pr-2">Bundle(s)</th>
                              <th className="py-1 pr-2"># Entries</th>
                              <th className="py-1 pr-2">Keeping</th>
                            </tr>
                          </thead>
                          <tbody>
                            {greigeDedupeResult.sample_groups.map((g, i) => (
                              <tr key={i} className="border-b border-slate-100" data-testid={`greige-dedupe-group-${i}`}>
                                <td className="py-1 pr-2 font-mono">{g.operation_name}</td>
                                <td className="py-1 pr-2 font-mono">{(g.bundles || []).join(', ')}</td>
                                <td className="py-1 pr-2 text-red-600 font-semibold">{g.total_entries}</td>
                                <td className="py-1 pr-2 font-mono text-emerald-700">{g.kept_entry_id}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mb-6" data-testid="inhouse-status-heal-card">
              <CardHeader>
                <CardTitle className="text-lg">Fix Inhouse Status on SFG Inventory</CardTitle>
                <CardDescription>
                  A bundle transferred into the Inhouse section should read{' '}
                  <code className="text-[11px]">TRANSFERRED</code> in{' '}
                  <code className="text-[11px]">wms_sfg_inventory</code>, with Inhouse ops
                  (<code className="text-[11px]">FOLDED</code>/<code className="text-[11px]">PRESSED</code>)
                  tracked in <code className="text-[11px]">inhouse_bundle_receipts</code>. Legacy dual-writes
                  left those operational statuses on the SFG row, inflating SFG stock reports. This sets such rows
                  to <strong>TRANSFERRED</strong> when a matching Inhouse receipt exists, and <strong>flags</strong>{' '}
                  SFG-only rows for review (status untouched). Always <strong>Preview</strong> first.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleHealInhouseStatus(true)}
                    disabled={isHealingInhouse}
                    data-testid="inhouse-heal-preview-btn"
                    variant="outline"
                  >
                    {isHealingInhouse ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
                    ) : (
                      <><Database className="w-4 h-4 mr-2" /> Preview</>
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      if (window.confirm('Set the matched SFG rows to TRANSFERRED and flag SFG-only rows for review? An audit snapshot is saved.')) {
                        handleHealInhouseStatus(false);
                      }
                    }}
                    disabled={isHealingInhouse || !inhouseHealResult || (inhouseHealResult?.scanned || 0) === 0}
                    data-testid="inhouse-heal-apply-btn"
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Apply Fix
                  </Button>
                </div>

                {inhouseHealResult && (
                  <div
                    className="border rounded-lg p-4 space-y-3 text-sm bg-slate-50 border-slate-200"
                    data-testid="inhouse-heal-result"
                  >
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white rounded p-2 border">
                        <div className="text-[11px] text-slate-500">SFG rows scanned</div>
                        <div className="text-lg font-bold">{(inhouseHealResult.scanned || 0).toLocaleString()}</div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-[11px] text-slate-500">→ TRANSFERRED</div>
                        <div className="text-lg font-bold text-emerald-700">{(inhouseHealResult.converted_to_transferred || 0).toLocaleString()}</div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-[11px] text-slate-500">Flagged for review</div>
                        <div className="text-lg font-bold text-amber-600">{(inhouseHealResult.flagged_for_review || 0).toLocaleString()}</div>
                      </div>
                    </div>

                    {!inhouseHealResult.dry_run && (
                      <div className="text-emerald-700 font-medium" data-testid="inhouse-heal-applied-banner">
                        ✓ Fix applied.
                      </div>
                    )}

                    {inhouseHealResult.by_status && Object.keys(inhouseHealResult.by_status).length > 0 && (
                      <ul className="text-xs space-y-0.5">
                        {Object.entries(inhouseHealResult.by_status).map(([st, v]) => (
                          <li key={st} className="text-slate-600">
                            <span className="font-mono">{st}</span> — {v.convert} convertible · {v.flag} to review
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>


            <Card className="mb-6" data-testid="sfg-sku-heal-card">
              <CardHeader>
                <CardTitle className="text-lg">Backfill Blank SKU Code on SFG Inventory</CardTitle>
                <CardDescription>
                  Some <code className="text-[11px]">wms_sfg_inventory</code> rows (legacy / certain
                  transfer paths) landed with a blank <code className="text-[11px]">sku_code</code>,
                  so they show up empty in the SFG WMS collection and any report that reads the SKU.
                  This resolves the SKU <strong>from each bundle's origin</strong> using the exact same
                  resolver the normal put-away flow uses (inhouse / goods-transfer / FOB / greige /
                  packing → <code className="text-[11px]">item_barcode</code>), and hydrates blank
                  design/shade/size too. Rows whose origin can't be found are left untouched. Always{' '}
                  <strong>Preview</strong> first.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleHealSfgSku(true)}
                    disabled={isHealingSfgSku}
                    data-testid="sfg-sku-heal-preview-btn"
                    variant="outline"
                  >
                    {isHealingSfgSku ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
                    ) : (
                      <><Database className="w-4 h-4 mr-2" /> Preview</>
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      if (window.confirm('Backfill sku_code (and blank design/shade/size) from origin for all resolvable rows? An audit snapshot is saved.')) {
                        handleHealSfgSku(false);
                      }
                    }}
                    disabled={isHealingSfgSku || !sfgSkuHealResult || (sfgSkuHealResult?.resolvable || 0) === 0}
                    data-testid="sfg-sku-heal-apply-btn"
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Apply Backfill
                  </Button>
                </div>

                {sfgSkuHealResult && (
                  <div
                    className="border rounded-lg p-4 space-y-3 text-sm bg-slate-50 border-slate-200"
                    data-testid="sfg-sku-heal-result"
                  >
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-white rounded p-2 border">
                        <div className="text-[11px] text-slate-500">Blank SKU rows</div>
                        <div className="text-lg font-bold">{(sfgSkuHealResult.total_blank || 0).toLocaleString()}</div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-[11px] text-slate-500">Resolvable</div>
                        <div className="text-lg font-bold text-emerald-700">{(sfgSkuHealResult.resolvable || 0).toLocaleString()}</div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-[11px] text-slate-500">Unresolvable</div>
                        <div className="text-lg font-bold text-amber-600">{(sfgSkuHealResult.unresolvable || 0).toLocaleString()}</div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-[11px] text-slate-500">{sfgSkuHealResult.dry_run ? 'Remaining' : 'Updated'}</div>
                        <div className="text-lg font-bold text-blue-700">{((sfgSkuHealResult.dry_run ? sfgSkuHealResult.remaining_after : sfgSkuHealResult.updated) || 0).toLocaleString()}</div>
                      </div>
                    </div>

                    {!sfgSkuHealResult.dry_run && (
                      <div className="text-emerald-700 font-medium" data-testid="sfg-sku-heal-applied-banner">
                        ✓ Backfilled {(sfgSkuHealResult.updated || 0).toLocaleString()} SKU code(s). {(sfgSkuHealResult.remaining_after || 0).toLocaleString()} still blank.
                      </div>
                    )}

                    {sfgSkuHealResult.samples?.resolved?.length > 0 && (
                      <div>
                        <div className="text-[11px] font-semibold text-slate-500 mb-1">Sample resolved (from origin)</div>
                        <ul className="text-xs space-y-0.5">
                          {sfgSkuHealResult.samples.resolved.map((s) => (
                            <li key={s.bundle_code} className="text-slate-600 font-mono">
                              {s.bundle_code} → <span className="text-emerald-700">{s.sku_code}</span>{' '}
                              <span className="text-slate-400">({s.from})</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>


            {/* === Sub-section: 🚚 Migrations === */}
            <div className="mb-4 mt-8 first:mt-0">
              <div className="flex items-center justify-between border-b-2 border-slate-300 pb-2 mb-4">
                <h2 className="text-xl font-bold text-slate-800">🚚 Migrations</h2>
                <span className="text-xs text-slate-500">Low-frequency data backfills, refreshes and index rebuilds.</span>
              </div>
            </div>

            {/* Refresh Inventory Item Details Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Refresh FG Inventory Item Details</CardTitle>
                <CardDescription>
                  Corrects wrong item_barcode, design_name, shade, and size in wms_fg_inventory using the authoritative data from dispatch_received_bundle_usns. Run "Preview" first to see how many records are affected.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleRefreshInventoryDetails(true)}
                    disabled={isRefreshingInventory}
                    variant="outline"
                    data-testid="refresh-inventory-preview-btn"
                  >
                    {isRefreshingInventory ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Scanning...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Preview (Dry Run)
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleRefreshInventoryDetails(false)}
                    disabled={isRefreshingInventory || !inventoryRefreshResult?.dry_run}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="refresh-inventory-apply-btn"
                  >
                    {isRefreshingInventory ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Apply Corrections
                      </>
                    )}
                  </Button>
                </div>

                {inventoryRefreshResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${inventoryRefreshResult.error ? 'bg-red-50 border-red-200' : inventoryRefreshResult.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                    <p className={`text-sm font-medium ${inventoryRefreshResult.error ? 'text-red-800' : inventoryRefreshResult.dry_run ? 'text-blue-800' : 'text-green-800'}`}>
                      {inventoryRefreshResult.running ? 'In Progress' : inventoryRefreshResult.dry_run ? 'Preview Result' : 'Applied Successfully'}: {inventoryRefreshResult.message}
                    </p>
                    
                    {inventoryRefreshResult.total_inventory > 0 && inventoryRefreshResult.running && (
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min((inventoryRefreshResult.total_processed / inventoryRefreshResult.total_inventory) * 100, 100)}%` }}
                        />
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Total Scanned</p>
                        <p className="font-semibold" data-testid="refresh-total">{inventoryRefreshResult.total_processed?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">{inventoryRefreshResult.dry_run ? 'Would Correct' : 'Corrected'}</p>
                        <p className="font-semibold text-orange-700" data-testid="refresh-corrected">{inventoryRefreshResult.total_corrected?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Already Correct</p>
                        <p className="font-semibold text-green-700" data-testid="refresh-correct">{inventoryRefreshResult.total_already_correct?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">No Source Data</p>
                        <p className="font-semibold text-gray-500" data-testid="refresh-not-found">{inventoryRefreshResult.total_not_found?.toLocaleString()}</p>
                        {inventoryRefreshResult.total_not_found > 0 && !inventoryRefreshResult.running && (
                          <button
                            onClick={async () => {
                              try {
                                const res = await apiClient.get('/wms/fg/put-away/refresh-inventory-no-source-data/download', { responseType: 'blob' });
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'no_source_data_usns.csv';
                                a.click();
                                window.URL.revokeObjectURL(url);
                              } catch { toast.error('Download failed'); }
                            }}
                            className="text-xs text-blue-600 hover:underline mt-1 inline-block cursor-pointer"
                            data-testid="download-no-source-btn"
                          >
                            Download CSV
                          </button>
                        )}
                      </div>
                    </div>

                    {inventoryRefreshResult.sample_corrections?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-600 mb-2">Sample Corrections (first {inventoryRefreshResult.sample_corrections.length}):</p>
                        <div className="max-h-60 overflow-y-auto space-y-2">
                          {inventoryRefreshResult.sample_corrections.map((c, i) => (
                            <div key={i} className="text-xs bg-white border rounded p-2 flex flex-wrap gap-x-4 gap-y-1">
                              <span className="font-mono font-semibold text-gray-700">{c.usn_code}</span>
                              <span className="text-red-600">Before: {c.before.design_name} / {c.before.shade} / {c.before.item_barcode}</span>
                              <span className="text-green-600">After: {c.after.design_name} / {c.after.shade} / {c.after.item_barcode}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Backfill Pack Barcode from USN Code Card (Re-Sticker Queue fix) */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Backfill Pack Barcode (Re-Sticker Queue Repair)</CardTitle>
                <CardDescription>
                  Some legacy FG inventory rows have a <code className="font-mono">usn_code</code> but no
                  <code className="font-mono"> pack_barcode</code> field — they show up with blank Pack/USN cells
                  in the Re-Sticker Queue. This tool copies <code>usn_code → pack_barcode</code> for every
                  affected row. Idempotent: re-running flips 0 rows.
                  Run <strong>Preview</strong> first to see how many rows are affected.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleBackfillPackBarcode(true)}
                    disabled={isBackfillingPackBarcode}
                    variant="outline"
                    data-testid="backfill-pack-barcode-preview-btn"
                  >
                    {isBackfillingPackBarcode ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Scanning...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Preview (Dry Run)
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleBackfillPackBarcode(false)}
                    disabled={isBackfillingPackBarcode || !backfillPackBarcodeResult?.dry_run}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="backfill-pack-barcode-apply-btn"
                  >
                    {isBackfillingPackBarcode ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Apply Backfill
                      </>
                    )}
                  </Button>
                </div>

                {backfillPackBarcodeResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${backfillPackBarcodeResult.error ? 'bg-red-50 border-red-200' : backfillPackBarcodeResult.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                    <p className={`text-sm font-medium ${backfillPackBarcodeResult.error ? 'text-red-800' : backfillPackBarcodeResult.dry_run ? 'text-blue-800' : 'text-green-800'}`}>
                      {backfillPackBarcodeResult.error
                        ? `Error: ${backfillPackBarcodeResult.error}`
                        : backfillPackBarcodeResult.dry_run
                          ? (backfillPackBarcodeResult.viable_rows > 0
                              ? `Preview: would flip ${backfillPackBarcodeResult.viable_rows.toLocaleString()} row(s). Click "Apply Backfill" to commit.`
                              : 'Preview: no rows need backfilling — collection is already clean.')
                          : `Applied: flipped ${backfillPackBarcodeResult.updated.toLocaleString()} row(s).`}
                    </p>
                    {!backfillPackBarcodeResult.error && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500">Total Rows</p>
                          <p className="font-semibold" data-testid="backfill-pb-total">{backfillPackBarcodeResult.total_rows?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">{backfillPackBarcodeResult.dry_run ? 'Viable' : 'Flipped'}</p>
                          <p className="font-semibold text-orange-700" data-testid="backfill-pb-viable">
                            {(backfillPackBarcodeResult.dry_run ? backfillPackBarcodeResult.viable_rows : backfillPackBarcodeResult.updated)?.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Un-fixable (no usn_code either)</p>
                          <p className="font-semibold text-red-700" data-testid="backfill-pb-unfixable">{backfillPackBarcodeResult.un_fixable_rows?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Mode</p>
                          <p className="font-semibold">{backfillPackBarcodeResult.dry_run ? 'DRY RUN' : 'APPLY'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Heal Stuck PICKED_FOR_COMBO_V2 Bundles Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Release Stuck "Picked for Combo V2" Bundles</CardTitle>
                <CardDescription>
                  When a Combo V2 SFG pick slip is cancelled, bundles that were already on the
                  distributor floor (<code className="font-mono">status = PICKED_FOR_COMBO_V2</code>)
                  used to stay tagged with the cancelled plan indefinitely. The slip-cancel code
                  has been patched, but bundles already stuck in this state need a one-shot release.
                  Idempotent — re-running flips 0 rows.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleHealV2StuckBundles(true)}
                    disabled={isHealingV2Bundles}
                    variant="outline"
                    data-testid="heal-v2-bundles-preview-btn"
                  >
                    {isHealingV2Bundles ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Scanning...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Preview (Dry Run)
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleHealV2StuckBundles(false)}
                    disabled={isHealingV2Bundles || !healV2BundlesResult?.dry_run || healV2BundlesResult?.stuck_bundles_found === 0}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="heal-v2-bundles-apply-btn"
                  >
                    {isHealingV2Bundles ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Release Bundles
                      </>
                    )}
                  </Button>
                </div>

                {healV2BundlesResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${healV2BundlesResult.error ? 'bg-red-50 border-red-200' : healV2BundlesResult.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                    <p className={`text-sm font-medium ${healV2BundlesResult.error ? 'text-red-800' : healV2BundlesResult.dry_run ? 'text-blue-800' : 'text-green-800'}`}>
                      {healV2BundlesResult.error
                        ? `Error: ${healV2BundlesResult.error}`
                        : (healV2BundlesResult.message
                            || `Released ${(healV2BundlesResult.updated || 0).toLocaleString()} bundle(s).`)}
                    </p>
                    {!healV2BundlesResult.error && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500">Stuck Bundles Found</p>
                          <p className="font-semibold text-orange-700" data-testid="heal-v2-found">
                            {(healV2BundlesResult.stuck_bundles_found ?? 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Released</p>
                          <p className="font-semibold text-green-700" data-testid="heal-v2-updated">
                            {(healV2BundlesResult.updated ?? 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Mode</p>
                          <p className="font-semibold">{healV2BundlesResult.dry_run ? 'DRY RUN' : 'APPLY'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Heal Phantom Replaced Stock — old-USN put-aways where canonical was picked separately */}
            <Card className="mb-6" data-testid="heal-phantom-card">
              <CardHeader>
                <CardTitle className="text-lg">Heal Phantom Stock (Replaced USNs)</CardTitle>
                <CardDescription>
                  Finds <code>wms_fg_inventory</code> rows that are still <strong>STORED</strong> but whose
                  USN has been deactivated by a replacement (operator pasted the OLD half-printed sticker
                  before reprint). For each row: if the canonical USN has no inventory row → rename in
                  place; if canonical already has its own STORED/PICKED row → mark phantom
                  <code className="ml-1">VOID_REPLACED</code> with audit fields.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => handleHealPhantomReplacedStock(true)}
                          disabled={isHealingPhantom} variant="outline"
                          data-testid="heal-phantom-preview-btn">
                    {isHealingPhantom ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
                    ) : (<><Search className="w-4 h-4 mr-2" /> Preview (Dry Run)</>)}
                  </Button>
                  <Button onClick={() => handleHealPhantomReplacedStock(false)}
                          disabled={isHealingPhantom
                                    || !healPhantomResult?.summary
                                    || ((healPhantomResult.summary.renamed_in_place || 0)
                                        + (healPhantomResult.summary.voided || 0)) === 0
                                    || !healPhantomResult?.summary?.dry_run}
                          className="bg-orange-600 hover:bg-orange-700"
                          data-testid="heal-phantom-apply-btn">
                    {isHealingPhantom ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Healing…</>
                    ) : (<>Apply Heal</>)}
                  </Button>
                  <Button onClick={() => handleHealPhantomCSV(true)}
                          variant="outline" data-testid="heal-phantom-csv-preview-btn">
                    Download Preview CSV
                  </Button>
                  <Button onClick={() => handleHealPhantomCSV(false)}
                          variant="outline" data-testid="heal-phantom-csv-committed-btn"
                          disabled={!healPhantomResult || healPhantomResult?.summary?.dry_run !== false}>
                    Download Committed CSV
                  </Button>
                </div>

                {healPhantomResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${
                    healPhantomResult.error ? 'bg-red-50 border-red-200'
                    : healPhantomResult.summary?.dry_run ? 'bg-blue-50 border-blue-200'
                    : 'bg-green-50 border-green-200'
                  }`} data-testid="heal-phantom-result">
                    {healPhantomResult.error ? (
                      <p className="text-sm font-medium text-red-800">
                        Error: {healPhantomResult.error}
                      </p>
                    ) : (
                      <>
                        <p className={`text-sm font-medium ${
                          healPhantomResult.summary.dry_run ? 'text-blue-800' : 'text-green-800'
                        }`}>
                          {healPhantomResult.summary.dry_run
                            ? `Preview: ${healPhantomResult.summary.renamed_in_place.toLocaleString()} to rename + `
                              + `${healPhantomResult.summary.voided.toLocaleString()} to void`
                            : `Healed: ${healPhantomResult.summary.renamed_in_place.toLocaleString()} renamed + `
                              + `${healPhantomResult.summary.voided.toLocaleString()} voided`}
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                          <div><p className="text-gray-500">Scanned</p>
                            <p className="font-semibold">{healPhantomResult.summary.scanned.toLocaleString()}</p></div>
                          <div><p className="text-gray-500">Renamed</p>
                            <p className="font-semibold">{healPhantomResult.summary.renamed_in_place.toLocaleString()}</p></div>
                          <div><p className="text-gray-500">Voided</p>
                            <p className="font-semibold">{healPhantomResult.summary.voided.toLocaleString()}</p></div>
                          <div><p className="text-gray-500">No canonical</p>
                            <p className="font-semibold">{healPhantomResult.summary.skipped_no_canonical.toLocaleString()}</p></div>
                          <div><p className="text-gray-500">Mode</p>
                            <p className="font-semibold">{healPhantomResult.summary.dry_run ? 'DRY RUN' : 'APPLIED'}</p></div>
                        </div>
                        {healPhantomResult.sample_healed?.length > 0 && (
                          <details className="text-xs">
                            <summary className="cursor-pointer text-stone-700 font-semibold">
                              Sample ({healPhantomResult.sample_healed.length} of {healPhantomResult.healed_count})
                            </summary>
                            <div className="mt-2 max-h-48 overflow-auto space-y-1 font-mono">
                              {healPhantomResult.sample_healed.map((r, idx) => (
                                <div key={idx} className="border-b py-1">
                                  <span className="text-rose-700">{r.old_usn}</span>
                                  {' → '}
                                  <span className="text-emerald-700">{r.canonical_usn}</span>
                                  <span className="text-stone-500"> · {r.bin_code} · {r.sku_code}</span>
                                  <span className="ml-2 text-xs text-amber-700">[{r.action}]</span>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Fix Missing Dispatch Receipts Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Fix Missing Dispatch Receipts</CardTitle>
                <CardDescription>
                  Finds bundles from completed internal transfers that are missing from dispatch_received_bundle_usns and creates the missing records. 
                  This ensures all transferred bundles are properly tracked in the dispatch receipt system.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleFixDispatchReceipts(true)}
                    disabled={isFixingDispatchReceipts}
                    variant="outline"
                    data-testid="dispatch-receipt-preview-btn"
                  >
                    {isFixingDispatchReceipts ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Scanning...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Preview (Dry Run)
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleFixDispatchReceipts(false)}
                    disabled={isFixingDispatchReceipts || !dispatchReceiptResult?.dry_run}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="dispatch-receipt-apply-btn"
                  >
                    {isFixingDispatchReceipts ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Fixing...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Apply Fix
                      </>
                    )}
                  </Button>
                </div>

                {dispatchReceiptResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${dispatchReceiptResult.error ? 'bg-red-50 border-red-200' : dispatchReceiptResult.missing_count === 0 ? 'bg-green-50 border-green-200' : dispatchReceiptResult.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                    <p className={`text-sm font-medium ${dispatchReceiptResult.error ? 'text-red-800' : dispatchReceiptResult.missing_count === 0 ? 'text-green-800' : dispatchReceiptResult.dry_run ? 'text-blue-800' : 'text-green-800'}`}>
                      {dispatchReceiptResult.error ? `Error: ${dispatchReceiptResult.error}` : dispatchReceiptResult.message}
                    </p>
                    
                    {dispatchReceiptResult.total_checked > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500">Total Checked</p>
                          <p className="font-semibold" data-testid="dispatch-total">{dispatchReceiptResult.total_checked?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Missing</p>
                          <p className="font-semibold text-orange-700" data-testid="dispatch-missing">{dispatchReceiptResult.missing_count?.toLocaleString()}</p>
                        </div>
                        {dispatchReceiptResult.fixed_count !== undefined && (
                          <div>
                            <p className="text-gray-500">Fixed</p>
                            <p className="font-semibold text-green-700" data-testid="dispatch-fixed">{dispatchReceiptResult.fixed_count?.toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {dispatchReceiptResult.affected_transfers?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-600 mb-2">Affected Transfers:</p>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {dispatchReceiptResult.affected_transfers.map((t, i) => (
                            <div key={i} className="text-xs bg-white border rounded p-2 flex justify-between items-center">
                              <span className="font-mono font-semibold text-gray-700">{t.transfer_id}</span>
                              <span className="text-gray-500">from {t.from_section}</span>
                              <span className="text-orange-600 font-medium">{t.missing_count} missing</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>


            {/* Recreate Database Indexes */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Recreate Database Indexes</CardTitle>
                <CardDescription>
                  Rebuild all database indexes. Required after a backup restore to prevent slow queries and timeouts.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleRecreateIndexes}
                  disabled={isRecreatingIndexes}
                  data-testid="recreate-indexes-btn"
                  variant="outline"
                >
                  {isRecreatingIndexes ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Rebuilding Indexes...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Recreate Indexes
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* === Sub-section: 🔄 Reconcile === */}
            <div className="mb-4 mt-8 first:mt-0">
              <div className="flex items-center justify-between border-b-2 border-slate-300 pb-2 mb-4">
                <h2 className="text-xl font-bold text-slate-800">🔄 Reconcile</h2>
                <span className="text-xs text-slate-500">Data-consistency fixes — count mismatches, duplicates, orphaned references.</span>
              </div>
            </div>

            {/* V1 Consumed USNs vs SFG Put-Away Reconciliation (2026-02-05) */}
            <Card className="mb-6 border-amber-400" data-testid="reconcile-v1-putaway-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Reconcile V1 Consumed USNs vs SFG Put-Away
                </CardTitle>
                <CardDescription>
                  When excess V1 floor pieces are put back into SFG Storage, some consumed USNs may
                  now also be ACTIVE in SFG (same sticker in two places). This heal swaps each
                  conflicting consumed USN with a V1-pick alternate that is NOT in SFG — keeping
                  both the consumption log and the SFG inventory physically accurate.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-500 text-amber-700 hover:bg-amber-50"
                    disabled={isReconcilingV1PutAway}
                    onClick={() => handleReconcileV1PutAway(true)}
                    data-testid="reconcile-v1-putaway-dry-run-btn"
                  >
                    {isReconcilingV1PutAway ? 'Scanning…' : 'Dry Run (preview swaps)'}
                  </Button>
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700"
                    disabled={isReconcilingV1PutAway}
                    onClick={() => {
                      if (!confirm('This will rewrite consumed USNs in V1 sessions. A dry-run is strongly recommended first. Proceed?')) return;
                      handleReconcileV1PutAway(false);
                    }}
                    data-testid="reconcile-v1-putaway-apply-btn"
                  >
                    {isReconcilingV1PutAway ? 'Healing…' : 'Apply Heal'}
                  </Button>
                </div>

                {reconcileV1PutAwayResult && (
                  <div className="p-3 bg-slate-50 border rounded text-sm space-y-1" data-testid="reconcile-v1-putaway-result">
                    <div className="font-medium text-slate-800">
                      {reconcileV1PutAwayResult.dry_run ? 'Dry-Run Result' : 'Heal Result'}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div>Sessions scanned: <span className="font-semibold">{reconcileV1PutAwayResult.sessions_scanned}</span></div>
                      <div>With conflicts: <span className="font-semibold">{reconcileV1PutAwayResult.sessions_with_conflicts}</span></div>
                      <div>Total conflicts: <span className="font-semibold text-amber-700">{reconcileV1PutAwayResult.total_conflicts}</span></div>
                      <div>Healed: <span className="font-semibold text-emerald-700">{reconcileV1PutAwayResult.total_healed}</span></div>
                    </div>
                    {reconcileV1PutAwayResult.total_unresolved > 0 && (
                      <div className="text-xs text-red-600">
                        ⚠ {reconcileV1PutAwayResult.total_unresolved} conflict(s) could not be resolved (no alternate available in the pick pool).
                      </div>
                    )}
                    <div className="mt-2">{reconcileV1PutAwayResult.message}</div>
                    {(reconcileV1PutAwayResult.details || []).length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-slate-600 hover:underline">
                          Per-session detail ({reconcileV1PutAwayResult.details.length} shown)
                        </summary>
                        <div className="mt-2 max-h-60 overflow-auto space-y-2">
                          {reconcileV1PutAwayResult.details.map((d) => (
                            <div key={d.session_id} className="p-2 bg-white border rounded text-xs">
                              <div className="font-mono text-slate-700">{d.session_id} · {d.combo_barcode} · {d.combo_name}</div>
                              <div>Consumed total: {d.consumed_total} · Conflicts: {d.conflicts_found} · Healed: {d.healed} · Unresolved: {d.unresolved} · Pool: {d.pool_size}</div>
                              {(d.swaps || []).length > 0 && (
                                <div className="mt-1 text-[11px] text-emerald-700">
                                  Swaps: {d.swaps.map(s => `${s.from}→${s.to}`).join(', ')}
                                </div>
                              )}
                              {(d.unresolved_usns || []).length > 0 && (
                                <div className="mt-1 text-[11px] text-red-600">
                                  Unresolved: {d.unresolved_usns.join(', ')}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>


            {/* Orphan COMBO_ASSEMBLY Pick Slips — Reconcile Tool */}
            <Card className="mb-6 border-indigo-300">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-indigo-600" />
                  Reconcile Orphan COMBO Pick Slips
                </CardTitle>
                <CardDescription>
                  <code className="text-xs">PENDING</code> rows in <code className="text-xs">wms_pick_slips</code> with
                  <code className="text-xs"> trigger_type=COMBO_ASSEMBLY</code> whose parent combo group is already
                  <strong> COMPLETED / CANCELLED / missing</strong>. These rows are invisible to the auto-assign endpoint
                  (combos go through combo groups only). Options: <strong>Mirror Parent</strong> copies the parent&apos;s
                  final status; <strong>Cancel</strong> simply marks them as cancelled.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={handleScanOrphanCombos}
                    disabled={isLoadingOrphans}
                    variant="outline"
                    data-testid="scan-orphan-combos-btn"
                  >
                    {isLoadingOrphans ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                    ) : (
                      <><Search className="w-4 h-4 mr-2" />Scan Orphan Combo Slips</>
                    )}
                  </Button>
                  {orphanResult && orphanResult.total_orphans > 0 && (
                    <>
                      <Button
                        onClick={() => handleBulkReconcileOrphans('mirror_parent')}
                        disabled={isBulkReconcilingOrphans}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="bulk-mirror-orphan-btn"
                      >
                        {isBulkReconcilingOrphans ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                        Mirror All ({orphanResult.total_orphans})
                      </Button>
                      <Button
                        onClick={() => handleBulkReconcileOrphans('cancel')}
                        disabled={isBulkReconcilingOrphans}
                        variant="outline"
                        className="border-gray-400 text-gray-700"
                        data-testid="bulk-cancel-orphan-btn"
                      >
                        Cancel All
                      </Button>
                    </>
                  )}
                </div>

                {orphanResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${orphanResult.total_orphans === 0 ? 'bg-green-50 border-green-200' : 'bg-indigo-50 border-indigo-200'}`}>
                    <div className="flex items-center gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Pending COMBO slips:</span>{' '}
                        <span className="font-semibold">{orphanResult.pending_combo_total}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Orphans (need reconcile):</span>{' '}
                        <span className="font-semibold text-red-700" data-testid="orphan-count">{orphanResult.total_orphans}</span>
                      </div>
                    </div>
                    {orphanResult.total_orphans === 0 && (
                      <p className="text-sm text-green-700">✓ All pending combo slips are correctly linked to active groups.</p>
                    )}
                    {(orphanResult.orphans || []).length > 0 && (
                      <div className="overflow-x-auto border rounded bg-white max-h-96 overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-gray-50 z-10">
                            <TableRow>
                              <TableHead>Slip No</TableHead>
                              <TableHead>SKU</TableHead>
                              <TableHead>Priority</TableHead>
                              <TableHead>Triggered</TableHead>
                              <TableHead>Parent Session</TableHead>
                              <TableHead>Parent Status</TableHead>
                              <TableHead />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {orphanResult.orphans.map((o) => (
                              <TableRow key={o.slip_no} data-testid={`orphan-row-${o.slip_no}`}>
                                <TableCell className="font-mono text-xs">{o.slip_no}</TableCell>
                                <TableCell className="font-mono text-xs">{o.sku_code}</TableCell>
                                <TableCell>
                                  <Badge className={o.priority_order === 0 ? 'bg-red-100 text-red-700 border-0 text-xs' : 'bg-gray-100 text-gray-700 border-0 text-xs'}>
                                    {o.priority || (o.priority_order === 0 ? 'Highest' : 'Normal')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs">{(o.triggered_at || '').slice(0, 16).replace('T', ' ')}</TableCell>
                                <TableCell className="font-mono text-[10px]">{o.combo_session_id || '-'}</TableCell>
                                <TableCell>
                                  <Badge className={o.parent_group_status === 'MISSING' ? 'bg-red-100 text-red-700 border-0 text-xs' : 'bg-amber-100 text-amber-700 border-0 text-xs'}>
                                    {o.parent_group_status}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    {(o.parent_group_status === 'COMPLETED' || o.parent_group_status === 'CANCELLED') && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleReconcileOrphan(o.slip_no, 'mirror_parent')}
                                        disabled={reconcilingOrphanSlipNo === o.slip_no}
                                        className="h-7 text-xs border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                                        data-testid={`mirror-orphan-btn-${o.slip_no}`}
                                      >
                                        {reconcilingOrphanSlipNo === o.slip_no ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Mirror'}
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleReconcileOrphan(o.slip_no, 'cancel')}
                                      disabled={reconcilingOrphanSlipNo === o.slip_no}
                                      className="h-7 text-xs border-gray-400 text-gray-600 hover:bg-gray-100"
                                      data-testid={`cancel-orphan-btn-${o.slip_no}`}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}

                {bulkOrphanResult && (
                  <div className="border rounded-lg p-3 bg-green-50 border-green-200 text-sm">
                    <p className="font-medium text-green-800">
                      ✓ Bulk reconcile: {bulkOrphanResult.cancelled} cancelled, {bulkOrphanResult.mirrored_completed} mirrored, {bulkOrphanResult.skipped_non_orphan} skipped (non-orphan), {bulkOrphanResult.skipped_no_parent} skipped (no parent)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* DOC ↔ IT Duplicate Reconcile — P0 2026-03 */}
            <Card className="mb-6 border-amber-300">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Reconcile DOC / IT Duplicates
                </CardTitle>
                <CardDescription>
                  When Gate Inward links an Internal Transfer to an Inward Documentation entry,
                  the <strong>DOC wins</strong> — but historical records may have updated only the
                  consignment, leaving the linked IT unmirrored. Downstream SFG Put-Away validates
                  the IT, so a mismatched pair causes put-away rejection. Dry-run first, then apply —
                  fix copies <code className="text-xs">bundle_receipt_completed</code>,
                  {' '}<code className="text-xs">received_bundles</code>, and status from the DOC consignment
                  onto its linked internal_transfer.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={handleScanDocIt}
                    disabled={isScanningDocIt}
                    variant="outline"
                    data-testid="scan-doc-it-btn"
                  >
                    {isScanningDocIt ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                    ) : (
                      <><Search className="w-4 h-4 mr-2" />Scan DOC/IT Duplicates</>
                    )}
                  </Button>
                  {docItScan && docItScan.total_mismatched > 0 && (
                    <Button
                      onClick={handleFixDocIt}
                      disabled={isFixingDocIt}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="fix-doc-it-btn"
                    >
                      {isFixingDocIt ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                      Fix All ({docItScan.total_mismatched})
                    </Button>
                  )}
                </div>

                {docItScan && (
                  <div className={`border rounded-lg p-4 space-y-3 ${docItScan.total_mismatched === 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-center gap-4 text-sm flex-wrap">
                      <div>
                        <span className="text-gray-500">Linked pairs:</span>{' '}
                        <span className="font-semibold">{docItScan.total_linked_pairs}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Needs reconcile:</span>{' '}
                        <span className="font-semibold text-amber-700" data-testid="doc-it-mismatch-count">
                          {docItScan.total_mismatched}
                        </span>
                      </div>
                    </div>
                    {docItScan.total_mismatched === 0 && (
                      <p className="text-sm text-green-700">✓ All linked DOC ↔ IT pairs are in sync.</p>
                    )}
                    {(docItScan.mismatches || []).length > 0 && (
                      <div className="overflow-x-auto border rounded bg-white max-h-96 overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-gray-50 z-10">
                            <TableRow>
                              <TableHead>Entry ID</TableHead>
                              <TableHead>Doc Barcode</TableHead>
                              <TableHead>Section</TableHead>
                              <TableHead>Linked IT</TableHead>
                              <TableHead>Cons Status</TableHead>
                              <TableHead>IT Status</TableHead>
                              <TableHead>Receipt (C/IT)</TableHead>
                              <TableHead>Issue</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {docItScan.mismatches.map((m) => (
                              <TableRow key={m.entry_id} data-testid={`doc-it-row-${m.entry_id}`}>
                                <TableCell className="font-mono text-xs">{m.entry_id}</TableCell>
                                <TableCell className="font-mono text-xs">{m.document_barcode || '-'}</TableCell>
                                <TableCell className="text-xs">{m.section_name || '-'}</TableCell>
                                <TableCell className="font-mono text-xs">{m.linked_internal_transfer_id}</TableCell>
                                <TableCell>
                                  <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">
                                    {m.cons_status || '-'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge className={m.it_status === 'MISSING' ? 'bg-red-100 text-red-700 border-0 text-xs' : 'bg-gray-100 text-gray-700 border-0 text-xs'}>
                                    {m.it_status || '-'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs">
                                  {m.cons_bundle_receipt_completed ? '✓' : '✗'}
                                  {' / '}
                                  {m.it_bundle_receipt_completed === null ? '-' : m.it_bundle_receipt_completed ? '✓' : '✗'}
                                </TableCell>
                                <TableCell className="text-xs text-amber-700">{m.issue}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}

                {docItFix && (
                  <div className="border rounded-lg p-3 bg-green-50 border-green-200 text-sm">
                    <p className="font-medium text-green-800">
                      ✓ Reconciled: {docItFix.fixed} fixed, {docItFix.unchanged} already in sync, {docItFix.missing_it} skipped (missing IT)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Combo Component Picked-Bundles Mismatch — Reconcile inflated counts */}
            <Card className="mb-6 border-rose-300">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-rose-600" />
                  Reconcile Combo Component <code className="text-sm">picked_bundles</code>
                </CardTitle>
                <CardDescription>
                  Active combo component pick slips where <code className="text-xs">picked_bundles</code> exceeds the true
                  count of <code className="text-xs">picked_items ∪ skipped_bundles</code>. Historical double-skips (pre-idempotency-fix)
                  could have prematurely marked a component COMPLETED — showing a green check on mobile even though bundles
                  remained unpicked on the floor. Fix rewrites <code className="text-xs">picked_bundles</code> and the
                  component <code className="text-xs">status</code> to match ground truth.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={handleScanComboMismatch}
                    disabled={isScanningComboMismatch}
                    variant="outline"
                    data-testid="scan-combo-mismatch-btn"
                  >
                    {isScanningComboMismatch ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                    ) : (
                      <><Search className="w-4 h-4 mr-2" />Scan Combo Mismatches</>
                    )}
                  </Button>
                  {comboMismatchResult && comboMismatchResult.total_mismatched > 0 && (
                    <Button
                      onClick={handleFixComboMismatch}
                      disabled={isFixingComboMismatch}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      data-testid="fix-combo-mismatch-btn"
                    >
                      {isFixingComboMismatch ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                      Fix All ({comboMismatchResult.total_mismatched})
                    </Button>
                  )}
                </div>

                {comboMismatchResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${comboMismatchResult.total_mismatched === 0 ? 'bg-green-50 border-green-200' : 'bg-rose-50 border-rose-200'}`}>
                    <div className="flex items-center gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Mismatched components:</span>{' '}
                        <span className="font-semibold" data-testid="combo-mismatch-count">{comboMismatchResult.total_mismatched}</span>
                      </div>
                    </div>
                    {comboMismatchResult.total_mismatched === 0 && (
                      <p className="text-sm text-green-700">✓ All active combo components have correct picked_bundles counts.</p>
                    )}
                    {(comboMismatchResult.components || []).length > 0 && (
                      <div className="overflow-x-auto border rounded bg-white max-h-80 overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-gray-50 z-10">
                            <TableRow>
                              <TableHead>Group</TableHead>
                              <TableHead>Component</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Stored</TableHead>
                              <TableHead className="text-right">True (Picked+Skipped)</TableHead>
                              <TableHead className="text-right">Delta</TableHead>
                              <TableHead className="text-right">Required</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {comboMismatchResult.components.map((c) => (
                              <TableRow key={c.slip_id} data-testid={`combo-mismatch-row-${c.slip_id}`}>
                                <TableCell className="font-mono text-xs">{c.slip_group_id}</TableCell>
                                <TableCell className="text-xs">
                                  <div className="font-medium">{c.component_name}</div>
                                  <div className="font-mono text-gray-500">{c.component_barcode}</div>
                                </TableCell>
                                <TableCell>
                                  <Badge className="bg-orange-100 text-orange-700 border-0 text-xs">{c.status}</Badge>
                                </TableCell>
                                <TableCell className="text-right font-semibold">{c.stored_picked_bundles}</TableCell>
                                <TableCell className="text-right">
                                  <span className="text-gray-700">{c.true_progress}</span>
                                  <span className="text-[10px] text-gray-400"> ({c.actual_picked}P + {c.actual_skipped}S)</span>
                                </TableCell>
                                <TableCell className={`text-right font-bold ${c.delta > 0 ? 'text-red-600' : 'text-orange-600'}`}>
                                  {c.delta > 0 ? `+${c.delta}` : c.delta}
                                </TableCell>
                                <TableCell className="text-right">{c.required_bundles}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}
                {comboMismatchFixResult && (
                  <div className="border rounded-lg p-3 bg-green-50 border-green-200 text-sm">
                    <p className="font-medium text-green-800">
                      ✓ Fixed {comboMismatchFixResult.fixed} component(s)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bundle Consumption vs Pick-Slip Required Qty Reconcile */}
            <Card className="mb-6 border-fuchsia-300">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-fuchsia-600" />
                  Reconcile Bundle <code className="text-sm">consumption_by_session</code>
                </CardTitle>
                <CardDescription>
                  Bundle-level <code className="text-xs">consumption_by_session[session]</code> that disagrees with the
                  <code className="text-xs"> required_qty</code> stored on the corresponding pick slip. A pre-fix greedy
                  attribution bug made the first picked bundle absorb the entire session quantity, inflating its
                  consumption and starving downstream sessions of pieces that were physically still available.
                  Rows whose session has already declared leftover (physically reconciled) are skipped to preserve
                  supervisor edits.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={handleScanConsumption}
                    disabled={isScanningConsumption}
                    variant="outline"
                    data-testid="scan-consumption-btn"
                  >
                    {isScanningConsumption ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                    ) : (
                      <><Search className="w-4 h-4 mr-2" />Scan Consumption Mismatches</>
                    )}
                  </Button>
                  {consumptionResult && consumptionResult.total_mismatched > 0 && (
                    <Button
                      onClick={handleFixConsumption}
                      disabled={isFixingConsumption}
                      className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                      data-testid="fix-consumption-btn"
                    >
                      {isFixingConsumption ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                      Fix All ({consumptionResult.total_mismatched})
                    </Button>
                  )}
                </div>
                {consumptionResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${consumptionResult.total_mismatched === 0 ? 'bg-green-50 border-green-200' : 'bg-fuchsia-50 border-fuchsia-200'}`}>
                    <div className="flex items-center gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Mismatched rows:</span>{' '}
                        <span className="font-semibold" data-testid="consumption-mismatch-count">{consumptionResult.total_mismatched}</span>
                      </div>
                    </div>
                    {consumptionResult.total_mismatched === 0 && (
                      <p className="text-sm text-green-700">✓ All bundle consumption rows agree with their pick slip.</p>
                    )}
                    {(consumptionResult.rows || []).length > 0 && (
                      <div className="overflow-x-auto border rounded bg-white max-h-96 overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-gray-50 z-10">
                            <TableRow>
                              <TableHead>Bundle</TableHead>
                              <TableHead>Session</TableHead>
                              <TableHead>Slip</TableHead>
                              <TableHead className="text-right">Logged</TableHead>
                              <TableHead className="text-right">Slip Req</TableHead>
                              <TableHead className="text-right">Delta</TableHead>
                              <TableHead>Bundle Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {consumptionResult.rows.slice(0, 500).map((r) => (
                              <TableRow key={`${r.bundle_code}-${r.session_id}`} data-testid={`consumption-row-${r.bundle_code}`}>
                                <TableCell className="font-mono text-xs">{r.bundle_code}</TableCell>
                                <TableCell className="font-mono text-[10px]">{r.session_id}</TableCell>
                                <TableCell className="font-mono text-xs">{r.slip_no}</TableCell>
                                <TableCell className="text-right font-semibold">{r.logged_consumption}</TableCell>
                                <TableCell className="text-right">{r.slip_required_qty}</TableCell>
                                <TableCell className={`text-right font-bold ${r.delta > 0 ? 'text-red-600' : 'text-orange-600'}`}>
                                  {r.delta > 0 ? `+${r.delta}` : r.delta}
                                </TableCell>
                                <TableCell>
                                  <Badge className="bg-gray-100 text-gray-700 border-0 text-xs">{r.bundle_status}</Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {consumptionResult.rows.length > 500 && (
                          <p className="text-[11px] text-gray-500 px-2 py-1">+{consumptionResult.rows.length - 500} more rows not shown</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {consumptionFixResult && (
                  <div className="border rounded-lg p-3 bg-green-50 border-green-200 text-sm">
                    <p className="font-medium text-green-800">
                      ✓ Fixed {consumptionFixResult.fixed} consumption row(s)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Mismatched Inhouse Bundle Receipts — USN Count Reconciliation */}
            <Card className="mb-6 border-amber-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Mismatched Bundle USNs (Inhouse Receipts)
                </CardTitle>
                <CardDescription>
                  Finds inhouse bundle receipts where <code className="text-xs">usn_count</code> {'>'} actual USNs stored
                  (&quot;ghost pieces&quot; — physical stock tracked but no sticker allocated). Click scan to see the list,
                  then top-up individual bundles or all at once. Fresh PI USNs will be claimed from the pool and appended
                  to the bundle; next Folding print auto-prints the new stickers.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={handleScanMismatchedBundles}
                    disabled={isLoadingMismatched}
                    variant="outline"
                    data-testid="scan-mismatched-bundles-btn"
                  >
                    {isLoadingMismatched ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                    ) : (
                      <><Search className="w-4 h-4 mr-2" />Scan Mismatched Bundles</>
                    )}
                  </Button>
                  {mismatchedResult && (mismatchedResult.total_bundles || 0) > 0 && (
                    <Button
                      onClick={handleTopUpAllMismatched}
                      disabled={isToppingUpMismatched}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="topup-all-mismatched-btn"
                    >
                      {isToppingUpMismatched ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Topping Up...</>
                      ) : (
                        <><Zap className="w-4 h-4 mr-2" />Top-Up All ({mismatchedResult.total_missing_usns} USN)</>
                      )}
                    </Button>
                  )}
                </div>

                {mismatchedResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${mismatchedResult.total_bundles === 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-center gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Mismatched bundles:</span>{' '}
                        <span className="font-semibold" data-testid="mismatched-bundle-count">
                          {mismatchedResult.total_bundles}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Total missing USNs:</span>{' '}
                        <span className="font-semibold text-red-700" data-testid="mismatched-missing-count">
                          {mismatchedResult.total_missing_usns}
                        </span>
                      </div>
                    </div>
                    {(mismatchedResult.bundles || []).length > 0 && (
                      <div className="overflow-x-auto border rounded bg-white">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50">
                              <TableHead>Bundle Code</TableHead>
                              <TableHead>Sort No</TableHead>
                              <TableHead>Vendor</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Declared</TableHead>
                              <TableHead className="text-right">Actual</TableHead>
                              <TableHead className="text-right">Delta</TableHead>
                              <TableHead />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {mismatchedResult.bundles.map((b) => (
                              <TableRow key={b.bundle_code} data-testid={`mismatched-row-${b.bundle_code}`}>
                                <TableCell className="font-mono text-xs">{b.bundle_code}</TableCell>
                                <TableCell className="text-xs">{b.sort_no || '-'}</TableCell>
                                <TableCell className="text-xs">{b.vendor_name || '-'}</TableCell>
                                <TableCell>
                                  <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">
                                    {b.status || '-'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right font-semibold">{b.usn_count}</TableCell>
                                <TableCell className="text-right">{b.existing_usns}</TableCell>
                                <TableCell className={`text-right font-bold ${b.delta > 0 ? 'text-red-600' : 'text-orange-600'}`}>
                                  {b.delta > 0 ? `+${b.delta}` : b.delta}
                                </TableCell>
                                <TableCell>
                                  {b.delta > 0 ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleTopUpOneBundle(b.bundle_code)}
                                      className="h-7 text-xs border-amber-500 text-amber-700 hover:bg-amber-50"
                                      data-testid={`topup-btn-${b.bundle_code}`}
                                    >
                                      <Zap className="w-3 h-3 mr-1" />Top-Up
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-gray-400" title="Extra USNs — needs manual review">manual</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}

                {mismatchedApplyResult && (
                  <div className="border rounded-lg p-3 bg-green-50 border-green-200 text-sm">
                    <p className="font-medium text-green-800">
                      ✓ Topped up {mismatchedApplyResult.total_topped_up} USN(s) across {mismatchedApplyResult.total_bundles} bundle(s)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reconcile Slips Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Reconcile Put-Away Slips</CardTitle>
                <CardDescription>
                  Sync active slips with FG inventory. Auto-completes slips whose pieces were already put away via Table Clearance mode.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleReconcileSlips}
                  disabled={isReconciling}
                  data-testid="reconcile-slips-btn"
                >
                  {isReconciling ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Reconciling...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reconcile Slips with Inventory
                    </>
                  )}
                </Button>

                {reconcileResult && (
                  <div className={`border rounded-lg p-4 space-y-2 ${reconcileResult.genuinely_pending ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                    <p className={`text-sm font-medium ${reconcileResult.genuinely_pending ? 'text-blue-800' : 'text-green-800'}`}>{reconcileResult.message}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Slips Checked</p>
                        <p className="font-semibold">{reconcileResult.slips_checked}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Slips Updated</p>
                        <p className="font-semibold">{reconcileResult.slips_updated}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Slips Completed</p>
                        <p className="font-semibold text-green-700">{reconcileResult.slips_completed}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">USNs Synced</p>
                        <p className="font-semibold">{reconcileResult.usns_synced}</p>
                      </div>
                    </div>
                    {!reconcileResult.all_done && !reconcileResult.genuinely_pending && reconcileResult.remaining_slips > 0 && (
                      <p className="text-xs text-amber-600 mt-2">
                        More stale slips may exist. Click again to continue.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* WIP Assignments Reconciliation Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Reconcile WIP Assignments</CardTitle>
                <CardDescription>
                  Clean up piece processing assignments. Marks bundles as completed if they've already been put away (via slip or table clearance). Also removes duplicate records. Run this on production to fix inflated staff piece counts.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleReconcileWipAssignments}
                  disabled={isReconcilingWip}
                  data-testid="reconcile-wip-btn"
                >
                  {isReconcilingWip ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Reconciling...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reconcile WIP Assignments
                    </>
                  )}
                </Button>

                {wipReconcileResult && (
                  <div className="border rounded-lg p-4 space-y-2 bg-green-50 border-green-200">
                    <p className="text-sm font-medium text-green-800">{wipReconcileResult.message}</p>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Duplicates Removed</p>
                        <p className="font-semibold text-red-700" data-testid="wip-dupes-removed">{wipReconcileResult.duplicates_removed}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Assignments Completed</p>
                        <p className="font-semibold text-green-700" data-testid="wip-assignments-completed">{wipReconcileResult.assignments_completed}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Remaining Active</p>
                        <p className="font-semibold text-blue-700" data-testid="wip-remaining-active">{wipReconcileResult.remaining_active}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reconcile SFG vs WIP Status (Ghost Bundle Fix) */}
            <Card data-testid="tool-reconcile-sfg-wip" className="border-red-200">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  Fix Ghost Bundles (SFG Re-Allocation)
                </h3>
                <p className="text-xs text-muted-foreground">
                  Finds bundles showing as STORED/ALLOCATED in SFG but already picked in completed pick slips.
                  Fixes status to IN_WIP and cancels contaminated pending slips containing ghost bundles.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleReconcileWip(true)}
                    disabled={isReconcilingWip}
                    variant="outline"
                    size="sm"
                    data-testid="reconcile-wip-preview-btn"
                  >
                    {isReconcilingWip ? 'Scanning...' : 'Preview'}
                  </Button>
                  <Button
                    onClick={() => {
                      if (window.confirm('This will fix ghost bundles and cancel contaminated slips. Continue?')) {
                        handleReconcileWip(false);
                      }
                    }}
                    disabled={isReconcilingWip || !wipReconcileResult?.mismatches}
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="reconcile-wip-apply-btn"
                  >
                    {isReconcilingWip ? 'Fixing...' : 'Fix Ghost Bundles'}
                  </Button>
                </div>
                {wipReconcileResult && (
                  <div className={`mt-2 p-3 rounded-lg border ${wipReconcileResult.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                    {wipReconcileResult.dry_run ? (
                      <div className="text-xs text-blue-800 space-y-1">
                        <p className="font-medium">Preview Results:</p>
                        <p>Total picked in completed slips: {wipReconcileResult.total_picked_in_completed?.toLocaleString()}</p>
                        <p className="font-semibold text-red-700">Ghost bundles found: {wipReconcileResult.mismatches} ({wipReconcileResult.stored_ghosts} STORED, {wipReconcileResult.allocated_ghosts} ALLOCATED)</p>
                        {wipReconcileResult.contaminated_slips > 0 && (
                          <div className="mt-1">
                            <p className="font-semibold text-orange-700">Contaminated slips to cancel: {wipReconcileResult.contaminated_slips}</p>
                            {wipReconcileResult.contaminated_slip_details?.map(s => (
                              <p key={s.slip_no} className="text-xs text-orange-600 ml-2">
                                {s.slip_no} ({s.sku_code}) — {s.ghost_bundles?.length} ghost bundle(s)
                              </p>
                            ))}
                          </div>
                        )}
                        {wipReconcileResult.bundles?.length > 0 && (
                          <div className="mt-2 max-h-40 overflow-y-auto">
                            <p className="text-xs font-semibold mb-1">Ghost bundles (up to 100):</p>
                            {wipReconcileResult.bundles.map(b => (
                              <p key={b.bundle_code} className="text-xs text-blue-600">
                                {b.bundle_code} [{b.sfg_status}] — originally picked in {b.originally_picked_in}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-green-800">
                        <p className="font-medium">{wipReconcileResult.message}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>


            {/* Detect Duplicate Pick Slips */}
            <Card data-testid="tool-detect-duplicate-slips">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                  Detect Duplicate Pick Slips
                </h3>
                <p className="text-xs text-muted-foreground">
                  Finds active SFG pick slips whose bundles have already been through FG Put Away (checked via permanent FG put-away records).
                  Cancels fully-duplicate slips and marks their bundles as CONSUMED.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleDetectDuplicateSlips(true)}
                    disabled={isDetectingDuplicates}
                    variant="outline"
                    size="sm"
                    data-testid="detect-duplicates-preview-btn"
                  >
                    {isDetectingDuplicates ? 'Scanning...' : 'Preview'}
                  </Button>
                  <Button
                    onClick={() => handleDetectDuplicateSlips(false)}
                    disabled={isDetectingDuplicates || !duplicateSlipResult?.duplicate_slips}
                    size="sm"
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="detect-duplicates-apply-btn"
                  >
                    {isDetectingDuplicates ? 'Fixing...' : `Fix ${duplicateSlipResult?.duplicate_slips || 0} Slip(s)`}
                  </Button>
                </div>
                {duplicateSlipResult && (
                  <div className={`mt-2 p-3 rounded-lg border ${duplicateSlipResult.dry_run ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
                    {duplicateSlipResult.dry_run ? (
                      <div className="text-xs text-orange-800 space-y-1">
                        <p className="font-medium">Preview Results:</p>
                        <p className="font-semibold">Duplicate slips: {duplicateSlipResult.duplicate_slips} ({duplicateSlipResult.duplicate_bundles} bundles already in FG)</p>
                        <p className="text-gray-500">Total active slips checked: {duplicateSlipResult.total_active_slips}</p>
                        {duplicateSlipResult.affected_slips?.length > 0 && (
                          <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                            {duplicateSlipResult.affected_slips.map(s => (
                              <div key={s.slip_no} className="text-xs font-mono p-1.5 rounded bg-orange-100">
                                <span className="font-semibold">{s.slip_no}</span> — {s.design_name || s.sku_code} — {s.status}
                                <br />
                                <span className="text-orange-600">
                                  {s.duplicate_count}/{s.total_bundles} bundles in FG{s.all_duplicates ? ' (ALL)' : ''}:
                                </span>{' '}
                                {s.duplicate_bundles.slice(0, 3).join(', ')}
                                {s.duplicate_bundles.length > 3 && ` +${s.duplicate_bundles.length - 3} more`}
                              </div>
                            ))}
                          </div>
                        )}
                        {duplicateSlipResult.duplicate_slips === 0 && (
                          <p className="text-green-700 font-medium mt-1">No duplicate pick slips found. All clear!</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-green-800 font-medium">{duplicateSlipResult.message}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* === Sub-section: 🩹 Heal === */}
            <div className="mb-4 mt-8 first:mt-0">
              <div className="flex items-center justify-between border-b-2 border-slate-300 pb-2 mb-4">
                <h2 className="text-xl font-bold text-slate-800">🩹 Heal</h2>
                <span className="text-xs text-slate-500">Targeted repairs for specific stuck/corrupted records.</span>
              </div>
            </div>

            {/* Heal SFG SKU Collision — when a bundle barcode is reused for a different SKU */}
            <Card className="mb-6 border-amber-300" data-testid="heal-sku-collision-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-amber-600" />
                  Heal SFG SKU Collision
                </CardTitle>
                <CardDescription>
                  A bundle barcode was reprinted or reused for a <strong>different SKU</strong>.
                  The SFG put-away screen will now raise a <code className="text-xs">409 Bundle code collision</code>{' '}
                  error. Use this tool to archive the stale <code className="text-xs">wms_sfg_inventory</code> row
                  (snapshot goes to <code className="text-xs">wms_sfg_inventory_archive</code> with{' '}
                  <code className="text-xs">superseded_at/by/reason</code>) so the next scan accepts the new bundle cleanly.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Bundle code</Label>
                    <Input
                      type="text"
                      placeholder="BUN008527/26-27"
                      value={healBundleCode}
                      onChange={(e) => setHealBundleCode(e.target.value)}
                      className="font-mono"
                      data-testid="heal-sku-bundle-input"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Reason (required — becomes superseded_reason on the archive)</Label>
                    <Input
                      type="text"
                      placeholder="e.g. Bundle barcode reused for MC344154 — phantom MC378412 row from April-02 Combo Return"
                      value={healReason}
                      onChange={(e) => setHealReason(e.target.value)}
                      data-testid="heal-sku-reason-input"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={handleLookupHealBundle}
                      variant="outline"
                      disabled={!healBundleCode.trim() || isHealingSku}
                      data-testid="heal-sku-lookup-btn"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Look Up Bundle
                    </Button>
                    <Button
                      onClick={handleApplyHealSku}
                      disabled={!healBundleCode.trim() || !healReason.trim() || !healPreview || isHealingSku}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="heal-sku-apply-btn"
                    >
                      {isHealingSku ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Archiving…</>
                      ) : (
                        <><ShieldCheck className="w-4 h-4 mr-2" />Archive Stale Row</>
                      )}
                    </Button>
                  </div>
                </div>

                {healPreview && (
                  <div
                    className="border rounded-lg p-4 bg-amber-50 border-amber-200 space-y-2 text-sm"
                    data-testid="heal-sku-preview"
                  >
                    <div className="font-semibold text-amber-900">
                      Current SFG row — will be archived on Apply:
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-stone-500">SKU:</span>{' '}
                        <span className="font-mono font-semibold">{healPreview.sku_code || '—'}</span></div>
                      <div><span className="text-stone-500">Design:</span>{' '}
                        <span className="font-mono">{healPreview.design_name || '—'}</span></div>
                      <div><span className="text-stone-500">Shade/Size:</span>{' '}
                        <span className="font-mono">{healPreview.shade || '—'} / {healPreview.size || '—'}</span></div>
                      <div><span className="text-stone-500">Status:</span>{' '}
                        <span className="font-semibold">{healPreview.status || '—'}</span></div>
                      <div><span className="text-stone-500">Bin:</span>{' '}
                        <span className="font-mono">{healPreview.bin_location || '—'}</span></div>
                      <div><span className="text-stone-500">USN count:</span>{' '}
                        <span className="font-semibold">{healPreview.usn_count ?? 0}</span></div>
                      <div><span className="text-stone-500">Put-away date:</span>{' '}
                        <span className="font-mono">{healPreview.put_away_date || '—'}</span></div>
                      <div><span className="text-stone-500">Source section:</span>{' '}
                        <span>{healPreview.source_section || '—'}</span></div>
                    </div>
                  </div>
                )}

                {healResult && (
                  <div
                    className="border rounded-lg p-4 bg-green-50 border-green-200 space-y-2 text-sm"
                    data-testid="heal-sku-result"
                  >
                    <div className="flex items-center gap-2 font-semibold text-green-900">
                      <CheckCircle className="w-4 h-4" />
                      Archived successfully
                    </div>
                    <div className="text-xs text-stone-700">
                      Stale row moved to <code>wms_sfg_inventory_archive</code>. The next SFG
                      put-away scan of <code className="font-mono">{healResult.bundle_code}</code>{' '}
                      will create a fresh row with the incoming SKU.
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Heal Historical Reprint Slips — chunked rewrite of stale pack_barcodes */}
            <Card className="mb-6 border-purple-300" data-testid="heal-reprint-slips-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Printer className="w-5 h-5 text-purple-600" />
                  Heal Historical Reprint Slips
                </CardTitle>
                <CardDescription>
                  Scans non-packed FG pick slips for stale <code className="text-xs">pack_barcode</code> values
                  (left over from combo USN reprints done <strong>before the 2026-04-30 engine fix</strong>) and
                  rewrites them to the live successor USN. Runs in <strong>25-slip chunks</strong> to avoid
                  frontend timeouts. Always preview with <strong>Dry Run</strong> first.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2 flex-wrap items-center">
                  <Button
                    onClick={handleEstimateReprintHeal}
                    variant="outline"
                    disabled={isHealingReprints}
                    data-testid="estimate-reprint-heal-btn"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Estimate Scope
                  </Button>
                  <Button
                    onClick={() => runReprintHealChunks(true)}
                    variant="outline"
                    disabled={isHealingReprints}
                    data-testid="dry-run-reprint-heal-btn"
                  >
                    {isHealingReprints && reprintHealProgress.dry_run ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning…</>
                    ) : (
                      <><Eye className="w-4 h-4 mr-2" />Dry Run</>
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      if (!window.confirm(
                        'This will rewrite pack_barcode on historical pick slips.\n\n' +
                        'The write is idempotent but operates on real data. Continue?'
                      )) return;
                      runReprintHealChunks(false);
                    }}
                    disabled={isHealingReprints}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="apply-reprint-heal-btn"
                  >
                    {isHealingReprints && !reprintHealProgress.dry_run ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Healing…</>
                    ) : (
                      <><Wrench className="w-4 h-4 mr-2" />Apply Heal</>
                    )}
                  </Button>
                  {reprintHealCandidateCount !== null && (
                    <Badge variant="outline" className="text-xs">
                      {reprintHealCandidateCount} candidate slip(s)
                    </Badge>
                  )}
                </div>

                {(reprintHealProgress.running || reprintHealProgress.done || reprintHealProgress.error) && (
                  <div
                    className="border rounded-lg p-4 bg-purple-50 border-purple-200 space-y-2 text-sm"
                    data-testid="reprint-heal-progress"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-purple-900">
                        {reprintHealProgress.running
                          ? `${reprintHealProgress.dry_run ? 'Dry-running' : 'Healing'}…`
                          : reprintHealProgress.error
                            ? 'Heal failed'
                            : `${reprintHealProgress.dry_run ? 'Dry run' : 'Heal'} complete`}
                      </div>
                      <div className="text-xs text-stone-600 font-mono">
                        {reprintHealProgress.processed}
                        {reprintHealCandidateCount ? ` / ${reprintHealCandidateCount}` : ''} slip(s) scanned
                      </div>
                    </div>
                    {reprintHealCandidateCount > 0 && (
                      <div className="w-full h-2 bg-purple-100 rounded overflow-hidden">
                        <div
                          className="h-full bg-purple-600 transition-all"
                          style={{
                            width: `${Math.min(100, (reprintHealProgress.processed / reprintHealCandidateCount) * 100).toFixed(1)}%`,
                          }}
                        />
                      </div>
                    )}
                    <div className="text-xs text-stone-700 grid grid-cols-2 gap-1">
                      <span>Slips {reprintHealProgress.dry_run ? 'would heal' : 'healed'}:</span>
                      <span className="font-semibold">{reprintHealProgress.slips_rewritten}</span>
                      <span>USNs {reprintHealProgress.dry_run ? 'would rewrite' : 'rewritten'}:</span>
                      <span className="font-semibold">{reprintHealProgress.usns_rewritten}</span>
                    </div>
                    {reprintHealProgress.error && (
                      <div className="text-xs text-red-700">{reprintHealProgress.error}</div>
                    )}
                    {reprintHealLog.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-purple-700 hover:underline">
                          Show {reprintHealLog.length} affected slip(s)
                        </summary>
                        <div className="max-h-64 overflow-auto mt-2 border rounded bg-white text-[11px] font-mono">
                          <table className="w-full">
                            <thead className="bg-stone-100 sticky top-0">
                              <tr>
                                <th className="text-left p-1.5">Slip</th>
                                <th className="text-left p-1.5">Old USN</th>
                                <th className="text-left p-1.5">→ Live USN</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reprintHealLog.flatMap((ent) =>
                                ent.rewrites.map((rw, i) => (
                                  <tr key={`${ent.slip}-${i}`} className="border-t border-stone-200">
                                    <td className="p-1.5">{ent.slip}</td>
                                    <td className="p-1.5">{rw.old}</td>
                                    <td className="p-1.5">{rw.new}</td>
                                  </tr>
                                )),
                              )}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>


            {/* Diagnose & Repair Completed Slips Card */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-amber-600" />
                  Diagnose & Repair Completed Slips
                </CardTitle>
                <CardDescription>
                  Check specific slips for integrity issues, or scan all completed slips. Detects: empty placed_usns, 
                  mismatched USN codes, count mismatches, and all-skipped slips. Attempts to backfill from FG inventory.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Diagnose Specific Slip */}
                <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                  <p className="text-sm font-medium text-gray-700">Diagnose Specific Slip</p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter Slip ID (e.g., PAS-458E431E)"
                      value={diagnoseSlipId}
                      onChange={(e) => setDiagnoseSlipId(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleDiagnoseSlip()}
                      className="max-w-md"
                      data-testid="diagnose-slip-input"
                    />
                    <Button
                      onClick={handleDiagnoseSlip}
                      disabled={isDiagnosing || !diagnoseSlipId.trim()}
                      data-testid="diagnose-slip-btn"
                    >
                      {isDiagnosing ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Diagnosing...</>
                      ) : (
                        <><Search className="w-4 h-4 mr-2" /> Diagnose</>
                      )}
                    </Button>
                  </div>

                  {diagnosisResult && (
                    <div className={`border rounded-lg p-4 space-y-3 ${
                      diagnosisResult.overall_health === 'ok' ? 'bg-green-50 border-green-200' :
                      diagnosisResult.overall_health === 'critical' ? 'bg-red-50 border-red-200' :
                      'bg-amber-50 border-amber-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-mono font-semibold text-sm">{diagnosisResult.slip_id}</p>
                          <p className="text-xs text-gray-500">
                            Status: <span className="font-medium">{diagnosisResult.status}</span>
                            {diagnosisResult.assigned_to_name && <> &middot; Staff: {diagnosisResult.assigned_to_name}</>}
                            {diagnosisResult.completed_method && <> &middot; Method: {diagnosisResult.completed_method}</>}
                          </p>
                        </div>
                        <Badge variant="outline" className={
                          diagnosisResult.overall_health === 'ok' ? 'text-green-700 border-green-300' :
                          diagnosisResult.overall_health === 'critical' ? 'text-red-700 border-red-300' :
                          'text-amber-700 border-amber-300'
                        }>
                          {diagnosisResult.overall_health === 'ok' ? 'Healthy' :
                           diagnosisResult.overall_health === 'critical' ? 'Critical' : 'Issues Found'}
                        </Badge>
                      </div>

                      {diagnosisResult.summary && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500">Expected</p>
                            <p className="font-bold text-base">{diagnosisResult.summary.total_expected_usns}</p>
                          </div>
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500">Placed</p>
                            <p className="font-bold text-base">{diagnosisResult.summary.total_placed_usns}</p>
                          </div>
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500">In FG Inventory</p>
                            <p className="font-bold text-base">{diagnosisResult.summary.total_in_fg_inventory}</p>
                          </div>
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500">Placement %</p>
                            <p className="font-bold text-base">{diagnosisResult.summary.placement_coverage}</p>
                          </div>
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500">FG Inv %</p>
                            <p className="font-bold text-base">{diagnosisResult.summary.fg_inventory_coverage}</p>
                          </div>
                        </div>
                      )}

                      {diagnosisResult.issues?.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-red-700">Issues:</p>
                          {diagnosisResult.issues.map((issue, idx) => (
                            <p key={idx} className="text-xs text-red-600 flex items-start gap-1">
                              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" /> {issue}
                            </p>
                          ))}
                        </div>
                      )}

                      {diagnosisResult.items?.length > 0 && (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          <p className="text-xs font-medium text-gray-600">Items breakdown:</p>
                          {diagnosisResult.items.map((item, idx) => (
                            <div key={idx} className="text-xs bg-white/60 rounded p-2 border border-gray-200">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">{item.design_name}</span>
                                <Badge variant="outline" className="text-xs">{item.status}</Badge>
                              </div>
                              <div className="flex gap-4 mt-1 text-gray-600">
                                <span>Expected: {item.expected_usn_count}</span>
                                <span>Placed: {item.placed_usn_count}</span>
                                <span>Missing: {item.missing_usn_count}</span>
                                {item.foreign_usn_count > 0 && <span className="text-red-600">Foreign: {item.foreign_usn_count}</span>}
                                <span>In FG: {item.in_fg_inventory_count}</span>
                              </div>
                              {item.issues?.length > 0 && (
                                <div className="mt-1">
                                  {item.issues.map((issue, iidx) => (
                                    <p key={iidx} className="text-red-500 text-xs">{issue}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Force Downgrade - for slips where pieces are physically in WIP */}
                      {diagnosisResult.status === 'completed' && (
                        <div className="pt-2 border-t space-y-3">
                          <p className="text-xs text-gray-600">
                            Force downgrade: removes placed_usns for USNs NOT in FG inventory, 
                            sets slip back to in_progress so bundles reappear in WIP.
                          </p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleForceDowngrade(diagnosisResult.slip_id, true)}
                              disabled={isDowngrading}
                              data-testid="force-downgrade-preview-btn"
                            >
                              {isDowngrading ? (
                                <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Checking...</>
                              ) : (
                                <><Eye className="w-3 h-3 mr-1" /> Preview Downgrade</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleForceDowngrade(diagnosisResult.slip_id, false)}
                              disabled={isDowngrading || !downgradeResult || downgradeResult.items_downgraded === 0}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid="force-downgrade-apply-btn"
                            >
                              {isDowngrading ? (
                                <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Downgrading...</>
                              ) : (
                                <><Zap className="w-3 h-3 mr-1" /> Apply Downgrade</>
                              )}
                            </Button>
                          </div>

                          {downgradeResult && (
                            <div className={`border rounded-lg p-3 text-sm ${
                              downgradeResult.items_downgraded === 0
                                ? 'bg-green-50 border-green-200'
                                : downgradeResult.dry_run
                                  ? 'bg-blue-50 border-blue-200'
                                  : 'bg-green-50 border-green-200'
                            }`}>
                              <p className={`font-medium text-xs ${
                                downgradeResult.items_downgraded === 0 ? 'text-green-800' :
                                downgradeResult.dry_run ? 'text-blue-800' : 'text-green-800'
                              }`}>
                                {downgradeResult.message}
                              </p>
                              
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs">
                                <div>
                                  <p className="text-gray-500">Items Downgraded</p>
                                  <p className="font-bold text-red-700">{downgradeResult.items_downgraded}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">USNs Removed</p>
                                  <p className="font-bold text-red-700">{downgradeResult.usns_removed_from_placed}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">USNs Kept</p>
                                  <p className="font-bold text-green-700">{downgradeResult.usns_kept_in_placed}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">New Status</p>
                                  <p className="font-bold">{downgradeResult.new_status}</p>
                                </div>
                              </div>

                              {downgradeResult.item_details?.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {downgradeResult.item_details.map((d, idx) => (
                                    <div key={idx} className="text-xs flex justify-between items-center py-1 border-b border-gray-200 last:border-0">
                                      <span className="font-medium">{d.design_name}</span>
                                      <span className="text-gray-500">
                                        {d.truly_in_fg !== undefined ? `In FG: ${d.truly_in_fg}` : ''} 
                                        {d.not_in_fg !== undefined ? ` | Not in FG: ${d.not_in_fg}` : ''}
                                      </span>
                                      <span className={d.action?.includes('downgraded') ? 'text-red-600' : 'text-green-600'}>
                                        {d.action?.includes('downgraded') ? 'Downgraded' : 'Unchanged'}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {downgradeResult.items_downgraded === 0 && (
                                <div className="flex items-center gap-2 mt-2">
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                  <p className="text-xs text-green-800">All placed USNs are confirmed in FG inventory. No downgrade needed.</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Bulk Repair All */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Scan All Completed Slips</p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => handleRepairCompletedSlips(true)}
                      disabled={isRepairing}
                      data-testid="preview-repair-btn"
                    >
                      {isRepairing ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning...</>
                      ) : (
                        <><Eye className="w-4 h-4 mr-2" /> Preview (Dry Run)</>
                      )}
                    </Button>
                    <Button
                      onClick={() => handleRepairCompletedSlips(false)}
                      disabled={isRepairing || !repairResult || repairResult.affected_slips === 0}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="run-repair-btn"
                    >
                      {isRepairing ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Repairing...</>
                      ) : (
                        <><Zap className="w-4 h-4 mr-2" /> Apply Repair All</>
                      )}
                    </Button>
                  </div>
                </div>

                {repairResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${
                    repairResult.affected_slips === 0
                      ? 'bg-green-50 border-green-200'
                      : repairResult.dry_run
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-green-50 border-green-200'
                  }`}>
                    <p className={`text-sm font-medium ${
                      repairResult.affected_slips === 0
                        ? 'text-green-800'
                        : repairResult.dry_run ? 'text-blue-800' : 'text-green-800'
                    }`}>
                      {repairResult.message}
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Completed Slips Checked</p>
                        <p className="font-semibold" data-testid="repair-total-checked">{repairResult.total_completed_slips?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Affected Slips</p>
                        <p className="font-semibold text-amber-700" data-testid="repair-affected">{repairResult.affected_slips}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Repaired (Backfilled)</p>
                        <p className="font-semibold text-green-700" data-testid="repair-repaired">{repairResult.repaired_slips}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Downgraded</p>
                        <p className="font-semibold text-red-700" data-testid="repair-downgraded">{repairResult.downgraded_slips}</p>
                      </div>
                    </div>

                    {repairResult.details?.length > 0 && (
                      <div className="mt-2 max-h-48 overflow-y-auto">
                        <p className="text-xs text-gray-600 mb-1 font-medium">Affected slip details:</p>
                        {repairResult.details.map((d, idx) => (
                          <div key={idx} className="text-xs py-1 border-b border-gray-200 last:border-0 flex justify-between items-center gap-2">
                            <span className="font-mono text-gray-700">{d.slip_id}</span>
                            <span className="text-gray-500 truncate">{d.assigned_to_name || 'Unassigned'}</span>
                            <span className="text-gray-500 whitespace-nowrap">{d.items_affected} items</span>
                            {d.issues && <span className="text-gray-400 text-xs truncate">{d.issues.join(', ')}</span>}
                            <Badge variant="outline" className={
                              d.action?.includes('repair') ? 'text-green-700 border-green-300' : 'text-red-700 border-red-300'
                            }>
                              {d.action}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}

                    {repairResult.affected_slips === 0 && (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                        <p className="text-sm text-green-800">All completed slips have properly recorded USN placements.</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Fix Stuck Combo Label Sessions */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Fix Stuck Combo Label Sessions</CardTitle>
                <CardDescription>
                  Fixes combo assembly sessions stuck at Step 3 (Print Stickers). If stickers were already printed but the session didn't advance to Return Excess, this tool patches the session flag.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleFixStuckComboLabels}
                  disabled={isFixingComboLabels}
                  data-testid="fix-combo-labels-btn"
                >
                  {isFixingComboLabels ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Scanning Sessions...
                    </>
                  ) : (
                    <>
                      <Printer className="w-4 h-4 mr-2" />
                      Fix Stuck Sessions
                    </>
                  )}
                </Button>

                {comboLabelFixResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${comboLabelFixResult.fixed_count > 0 ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                    <p className={`text-sm font-medium ${comboLabelFixResult.fixed_count > 0 ? 'text-green-800' : 'text-blue-800'}`}>
                      {comboLabelFixResult.message}
                    </p>
                    {comboLabelFixResult.fixed_sessions?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-green-700">Fixed Sessions:</p>
                        {comboLabelFixResult.fixed_sessions.map(s => (
                          <p key={s.session_id} className="text-xs text-green-600">
                            {s.design_name} — {s.shade}/{s.size} ({s.total_items} items) [{s.session_id}]
                          </p>
                        ))}
                      </div>
                    )}
                    {comboLabelFixResult.still_stuck_sessions?.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-amber-700">Still Stuck (labels not marked as printed in DB):</p>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="text-xs h-7"
                            disabled={forceFixingId === 'all'}
                            onClick={handleForceFixAll}
                            data-testid="force-fix-all-btn"
                          >
                            {forceFixingId === 'all' ? 'Fixing All...' : `Force Fix All (${comboLabelFixResult.still_stuck_sessions.length})`}
                          </Button>
                        </div>
                        {comboLabelFixResult.still_stuck_sessions.map(s => (
                          <div key={s.session_id} className="flex items-center justify-between bg-amber-50 rounded p-2">
                            <p className="text-xs text-amber-800">
                              {s.design_name || s.combo_name} — {s.unprinted}/{s.total} unprinted [{s.session_id}]
                            </p>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="text-xs h-7 ml-2"
                              disabled={forceFixingId === s.session_id}
                              onClick={() => handleForceFixSession(s.session_id)}
                              data-testid={`force-fix-${s.session_id}`}
                            >
                              {forceFixingId === s.session_id ? 'Fixing...' : 'Force Fix'}
                            </Button>
                          </div>
                        ))}
                        <p className="text-xs text-gray-500 italic mt-1">
                          Use Force Fix only if stickers were physically printed but the system didn't register it.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            {/* === Sub-section: 🧹 Cleanup === */}
            <div className="mb-4 mt-8 first:mt-0">
              <div className="flex items-center justify-between border-b-2 border-slate-300 pb-2 mb-4">
                <h2 className="text-xl font-bold text-slate-800">🧹 Cleanup</h2>
                <span className="text-xs text-slate-500">Release stuck or ghost bundles/slips/roles back to a clean state.</span>
              </div>
            </div>

            {/* Stuck Pick Slips — Force Release Admin Tool */}
            <Card className="mb-6 border-red-300">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Unlock className="w-5 h-5 text-red-600" />
                  Force-Release Stuck SFG Pick Slips
                </CardTitle>
                <CardDescription>
                  SFG pick slips in <code className="text-xs">IN_PROGRESS</code> or <code className="text-xs">ASSIGNED</code> state
                  that have been stuck beyond a threshold (no bundle picked yet). These freeze the assigned picker&apos;s screen,
                  preventing them from receiving any new high-priority slip. Release them to return the slip to the queue.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3 flex-wrap items-end">
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Minimum age (hours)</Label>
                    <Input
                      type="number"
                      min={1}
                      value={stuckMinHours}
                      onChange={(e) => setStuckMinHours(parseInt(e.target.value) || 12)}
                      className="w-28"
                      data-testid="stuck-min-hours-input"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs mb-2 cursor-pointer">
                    <Checkbox
                      checked={stuckZeroOnly}
                      onCheckedChange={(v) => setStuckZeroOnly(!!v)}
                      data-testid="stuck-zero-only-toggle"
                    />
                    Only zero-picked
                  </label>
                  <Button
                    onClick={handleScanStuckSlips}
                    disabled={isLoadingStuck}
                    variant="outline"
                    data-testid="scan-stuck-slips-btn"
                  >
                    {isLoadingStuck ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                    ) : (
                      <><Search className="w-4 h-4 mr-2" />Scan Stuck Slips</>
                    )}
                  </Button>
                </div>

                {stuckResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${stuckResult.total_stuck === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-4 text-sm flex-wrap">
                      <div>
                        <span className="text-gray-500">Total stuck:</span>{' '}
                        <span className="font-semibold" data-testid="stuck-total-count">{stuckResult.total_stuck}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Regular:</span>{' '}
                        <span className="font-semibold">{stuckResult.regular_count}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Combo groups:</span>{' '}
                        <span className="font-semibold">{stuckResult.combo_count}</span>
                      </div>
                    </div>
                    {(stuckResult.slips || []).length > 0 && (
                      <div className="overflow-x-auto border rounded bg-white max-h-96 overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-gray-50 z-10">
                            <TableRow>
                              <TableHead>Slip / Group</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Priority</TableHead>
                              <TableHead>Stuck on (Picker)</TableHead>
                              <TableHead className="text-right">Age</TableHead>
                              <TableHead className="text-right">Picked</TableHead>
                              <TableHead />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {stuckResult.slips.map((s) => (
                              <TableRow key={`${s.slip_type}-${s.slip_no}`} data-testid={`stuck-row-${s.slip_no}`}>
                                <TableCell className="font-mono text-xs">{s.slip_no}</TableCell>
                                <TableCell>
                                  <Badge className={s.slip_type === 'COMBO_GROUP' ? 'bg-purple-100 text-purple-700 border-0 text-xs' : 'bg-blue-100 text-blue-700 border-0 text-xs'}>
                                    {s.slip_type}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge className="bg-orange-100 text-orange-700 border-0 text-xs">
                                    {s.status}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge className={s.priority_order === 0 ? 'bg-red-100 text-red-700 border-0 text-xs' : 'bg-gray-100 text-gray-700 border-0 text-xs'}>
                                    {s.priority || (s.priority_order === 0 ? 'Highest' : 'Normal')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs">
                                  <div className="font-medium">{s.picker_id || '-'}</div>
                                  {s.picker_name && <div className="text-gray-500">{s.picker_name}</div>}
                                </TableCell>
                                <TableCell className="text-right text-xs font-semibold text-red-700">
                                  {s.age_hours !== null && s.age_hours !== undefined ? `${s.age_hours}h` : '-'}
                                </TableCell>
                                <TableCell className="text-right text-xs">
                                  {s.bundles_picked}/{s.bundles_total}
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleReleaseStuckSlip(s, 'PENDING')}
                                      disabled={releasingSlipNo === s.slip_no}
                                      className="h-7 text-xs border-red-500 text-red-700 hover:bg-red-50"
                                      data-testid={`release-pending-btn-${s.slip_no}`}
                                    >
                                      {releasingSlipNo === s.slip_no ? <RefreshCw className="w-3 h-3 animate-spin" /> : <><Unlock className="w-3 h-3 mr-1" />Release</>}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleReleaseStuckSlip(s, 'CANCELLED')}
                                      disabled={releasingSlipNo === s.slip_no}
                                      className="h-7 text-xs border-gray-400 text-gray-600 hover:bg-gray-100"
                                      title="Mark as CANCELLED instead of returning to queue"
                                      data-testid={`release-cancel-btn-${s.slip_no}`}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cleanup Ghost WIP Bundles */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Cleanup Ghost WIP Bundles</CardTitle>
                <CardDescription>
                  Find and fix bundles stuck as IN_WIP in SFG inventory that have already been processed, put away, or have completed assignments. These "ghost" bundles show falsely in the WIP Stock Report as unassigned.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleCleanupGhostWip(true)}
                    disabled={isCleaningGhosts}
                    variant="outline"
                    data-testid="ghost-wip-preview-btn"
                  >
                    {isCleaningGhosts ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning...</>
                    ) : (
                      <><Search className="w-4 h-4 mr-2" />Preview (Dry Run)</>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleCleanupGhostWip(false)}
                    disabled={isCleaningGhosts || !ghostCleanupResult?.dry_run || ghostCleanupResult?.ghosts_found === 0}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="ghost-wip-apply-btn"
                  >
                    {isCleaningGhosts ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Fixing...</>
                    ) : (
                      <><Zap className="w-4 h-4 mr-2" />Fix Ghost Bundles</>
                    )}
                  </Button>
                </div>

                {ghostCleanupResult && (
                  <div className={`border rounded-lg p-4 space-y-3 ${ghostCleanupResult.error ? 'bg-red-50 border-red-200' : ghostCleanupResult.ghosts_found === 0 ? 'bg-green-50 border-green-200' : ghostCleanupResult.dry_run ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                    <p className={`text-sm font-medium ${ghostCleanupResult.error ? 'text-red-800' : ghostCleanupResult.ghosts_found === 0 ? 'text-green-800' : ghostCleanupResult.dry_run ? 'text-amber-800' : 'text-green-800'}`}>
                      {ghostCleanupResult.error || ghostCleanupResult.message}
                    </p>
                    {ghostCleanupResult.reason_breakdown && (
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div><p className="text-gray-500">Assignment Completed</p><p className="font-semibold">{ghostCleanupResult.reason_breakdown.assignment_completed}</p></div>
                        <div><p className="text-gray-500">Pieces Processed</p><p className="font-semibold">{ghostCleanupResult.reason_breakdown.pieces_processed}</p></div>
                        <div><p className="text-gray-500">Fully Put Away</p><p className="font-semibold">{ghostCleanupResult.reason_breakdown.fully_put_away}</p></div>
                      </div>
                    )}
                    {ghostCleanupResult.ghost_bundles?.length > 0 && ghostCleanupResult.dry_run && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-600 mb-2">Ghost Bundles (up to 100):</p>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {ghostCleanupResult.ghost_bundles.map((b, i) => (
                            <div key={i} className="text-xs bg-white border rounded p-2 flex justify-between">
                              <span className="font-mono font-semibold">{b.bundle_code}</span>
                              <span className="text-gray-500">{b.sku_code}</span>
                              <span className="text-blue-600">{b.usn_count} pcs</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {!ghostCleanupResult.dry_run && ghostCleanupResult.fixed != null && (
                      <p className="text-sm text-green-700 font-medium">
                        Successfully fixed {ghostCleanupResult.fixed} bundles (status changed to PROCESSED)
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reset Assigned Slips Tool */}
            <Card data-testid="tool-reset-assigned-slips">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold">Reset Assigned Pick Slips to Queue</h3>
                <p className="text-xs text-muted-foreground">
                  Moves all pre-assigned SFG pick slips back to PENDING in the priority queue. 
                  Pickers will be assigned slips based on priority when they request the next one.
                </p>
                <Button
                  onClick={handleResetAssignedSlips}
                  disabled={isResettingAssigned}
                  variant="outline"
                  size="sm"
                  data-testid="reset-assigned-slips-btn"
                >
                  {isResettingAssigned ? 'Resetting...' : 'Reset Assigned Slips'}
                </Button>
                {resetAssignedResult && (
                  <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm font-medium text-green-800">{resetAssignedResult.message}</p>
                    <div className="text-xs text-green-700 mt-1">
                      <p>Queue: {resetAssignedResult.queue_stats?.pending} pending, {resetAssignedResult.queue_stats?.in_progress} in progress</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cleanup User Roles */}
            <Card data-testid="tool-cleanup-user-roles">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Database className="w-4 h-4 text-orange-600" />
                  Cleanup User Roles
                </h3>
                <p className="text-xs text-muted-foreground">
                  Removes polluted entries (e.g. USN codes) from the Roles list, keeping only legitimate roles.
                </p>
                <div className="flex gap-2">
                  <Button onClick={() => handleCleanupRoles(true)} disabled={isCleaningRoles} variant="outline" size="sm" data-testid="cleanup-roles-preview-btn">
                    {isCleaningRoles ? 'Scanning...' : 'Preview'}
                  </Button>
                  <Button onClick={() => handleCleanupRoles(false)} disabled={isCleaningRoles || !cleanRolesResult?.garbage_count} size="sm" className="bg-orange-600 hover:bg-orange-700" data-testid="cleanup-roles-apply-btn">
                    {isCleaningRoles ? 'Cleaning...' : `Remove ${cleanRolesResult?.garbage_count || 0} Invalid Roles`}
                  </Button>
                </div>
                {cleanRolesResult && (
                  <div className={`mt-2 p-3 rounded-lg border ${cleanRolesResult.dry_run ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                    {cleanRolesResult.dry_run ? (
                      <div className="text-xs text-amber-800 space-y-1">
                        <p className="font-semibold text-red-700">Invalid roles found: {cleanRolesResult.garbage_count}</p>
                        {cleanRolesResult.samples?.length > 0 && (
                          <p>Samples: {cleanRolesResult.samples.slice(0, 10).join(', ')}{cleanRolesResult.garbage_count > 10 ? '...' : ''}</p>
                        )}
                        {cleanRolesResult.garbage_count === 0 && (
                          <p className="text-green-700 font-medium">No invalid roles. All clean!</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-green-800 font-medium">{cleanRolesResult.message}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Release ALLOCATED Bundles (Invisible in Reports) */}
            <Card data-testid="tool-release-orphaned-bundles">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-rose-600" />
                  Release ALLOCATED Bundles
                </h3>
                <p className="text-xs text-muted-foreground">
                  Finds bundles stuck in ALLOCATED status — invisible in both SFG Inventory and IN_WIP reports.
                  Shows associated slip info. On apply: reverts to STORED and cancels stale pick slips.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleReleaseOrphaned(true)}
                    disabled={isReleasingOrphaned}
                    variant="outline"
                    size="sm"
                    data-testid="release-orphaned-preview-btn"
                  >
                    {isReleasingOrphaned ? 'Scanning...' : 'Preview'}
                  </Button>
                  <Button
                    onClick={() => handleReleaseOrphaned(false)}
                    disabled={isReleasingOrphaned || !orphanedResult?.stuck_count}
                    size="sm"
                    className="bg-rose-600 hover:bg-rose-700"
                    data-testid="release-orphaned-apply-btn"
                  >
                    {isReleasingOrphaned ? 'Releasing...' : `Release ${orphanedResult?.stuck_count || 0} Bundles`}
                  </Button>
                </div>
                {orphanedResult && (
                  <div className={`mt-2 p-3 rounded-lg border ${orphanedResult.dry_run ? 'bg-rose-50 border-rose-200' : 'bg-green-50 border-green-200'}`}>
                    {orphanedResult.dry_run ? (
                      <div className="text-xs text-rose-800 space-y-1">
                        <p className="font-medium">Preview Results:</p>
                        <p className="font-semibold text-rose-700">ALLOCATED bundles found: {orphanedResult.stuck_count}</p>
                        {orphanedResult.sample_bundles?.length > 0 && (
                          <div className="mt-2 max-h-48 overflow-y-auto">
                            {orphanedResult.sample_bundles.map(b => (
                              <p key={b.bundle_code} className="text-xs text-rose-700 font-mono">
                                {b.bundle_code} — Slip: {b.slip_ref} ({b.slip_status}) — Bin: {b.bin_location}
                              </p>
                            ))}
                          </div>
                        )}
                        {orphanedResult.stuck_count === 0 && (
                          <p className="text-green-700 font-medium mt-1">No stuck ALLOCATED bundles. All clear!</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-green-800 font-medium">{orphanedResult.message}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* USN Double-Claim Integrity (2026-05-25) */}
            <Card data-testid="tool-usn-double-claim" className="border-red-300 bg-red-50/30">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-700" />
                  USN Double-Claim Integrity
                </h3>
                <p className="text-xs text-muted-foreground">
                  Detects USNs whose label-print claim overlaps an operational consumption
                  (picked into a packing slip, packed into a box, consumed by Combo V2, etc).
                  These leaks let one physical USN appear on two different label batches.
                </p>

                {/* 1. AUDIT */}
                <div className="border rounded-md p-3 bg-white">
                  <p className="text-xs font-medium text-red-800 mb-2">1. Scan for leaks (read-only)</p>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={handleAuditDoubleClaim}
                      disabled={isAuditingDC}
                      variant="outline"
                      size="sm"
                      data-testid="audit-double-claim-btn"
                    >
                      {isAuditingDC ? 'Scanning…' : 'Audit Double-Claimed USNs'}
                    </Button>
                    <Button
                      onClick={() => {
                        // Trigger CSV download via authenticated fetch.
                        (async () => {
                          try {
                            const res = await apiClient.get(
                              '/admin/export-double-claimed-usns-csv',
                              { responseType: 'blob' }
                            );
                            const blob = new Blob([res.data], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            const ts = new Date().toISOString().replace(/[:.]/g, '-');
                            a.download = `double_claimed_usns_${ts}.csv`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            URL.revokeObjectURL(url);
                            toast.success('CSV downloaded');
                          } catch (e) {
                            toast.error('Failed to download CSV');
                          }
                        })();
                      }}
                      variant="outline"
                      size="sm"
                      data-testid="audit-double-claim-export-csv-btn"
                    >
                      Download Full CSV Report
                    </Button>
                  </div>
                  {dcAuditResult && (
                    <div className="mt-2 p-3 rounded-lg border bg-amber-50 border-amber-200 text-xs space-y-1">
                      <p>Candidate pool (status=used_for_label): <span className="font-mono font-semibold">{dcAuditResult.candidate_pool_size}</span></p>
                      <p>Contaminated USNs found: <span className="font-mono font-semibold text-red-700">{dcAuditResult.contaminated_found}</span></p>
                      <p>Report rows returned: <span className="font-mono">{dcAuditResult.report_returned}</span></p>
                      <p>Guard-blocked events (live): <span className="font-mono">{dcAuditResult.guard_blocked_events_recorded}</span></p>
                      {dcAuditResult.by_claim_reason && Object.keys(dcAuditResult.by_claim_reason).length > 0 && (
                        <div className="mt-2">
                          <p className="font-medium mb-1">Tainted label sessions:</p>
                          <div className="max-h-32 overflow-y-auto font-mono">
                            {Object.entries(dcAuditResult.by_claim_reason).map(([reason, count]) => (
                              <p key={reason}>{reason}: <span className="text-red-700">{count}</span></p>
                            ))}
                          </div>
                        </div>
                      )}
                      {dcAuditResult.contaminated?.length > 0 && (
                        <div className="mt-2">
                          <p className="font-medium mb-1">Sample (first 20):</p>
                          <div className="max-h-48 overflow-y-auto font-mono text-[10px]">
                            {dcAuditResult.contaminated.slice(0, 20).map(c => (
                              <p key={c.usn_code} className="truncate">
                                {c.usn_code} ← {c.operationally_consumed_in} | {c.claim_reason}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 2. BULK HEAL */}
                <div className="border rounded-md p-3 bg-white">
                  <p className="text-xs font-medium text-red-800 mb-2">
                    2. Bulk heal — revert all leaks to <code>contaminated_double_claim</code>
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleHealDoubleClaimBulk(true)}
                      disabled={isHealingDCBulk}
                      variant="outline"
                      size="sm"
                      data-testid="heal-double-claim-bulk-preview-btn"
                    >
                      {isHealingDCBulk ? 'Working…' : 'Preview (dry run)'}
                    </Button>
                    <Button
                      onClick={() => handleHealDoubleClaimBulk(false)}
                      disabled={isHealingDCBulk || !dcHealResult?.contaminated_found}
                      size="sm"
                      className="bg-red-700 hover:bg-red-800"
                      data-testid="heal-double-claim-bulk-apply-btn"
                    >
                      {isHealingDCBulk ? 'Healing…' : `Apply heal (${dcHealResult?.contaminated_found || 0})`}
                    </Button>
                  </div>
                  {dcHealResult && (
                    <div className={`mt-2 p-3 rounded-lg border text-xs ${dcHealResult.dry_run ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                      {dcHealResult.dry_run ? (
                        <>
                          <p>Found: <span className="font-mono font-semibold">{dcHealResult.contaminated_found}</span></p>
                          <p>Would apply: <span className="font-mono font-semibold">{dcHealResult.would_apply}</span></p>
                        </>
                      ) : (
                        <p className="font-medium text-green-800">{dcHealResult.message}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* 3. SINGLE USN HEAL */}
                <div className="border rounded-md p-3 bg-white">
                  <p className="text-xs font-medium text-red-800 mb-2">3. Single USN revert</p>
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={singleDcUsnInput}
                      onChange={(e) => setSingleDcUsnInput(e.target.value)}
                      placeholder="USN code (e.g. 6XO5XXALX)"
                      className="border rounded px-2 py-1 text-xs flex-1 font-mono"
                      data-testid="heal-double-claim-single-input"
                    />
                    <Button
                      onClick={() => handleHealDoubleClaimSingle(true)}
                      disabled={isHealingSingleDC || !singleDcUsnInput.trim()}
                      variant="outline"
                      size="sm"
                      data-testid="heal-double-claim-single-preview-btn"
                    >
                      Preview
                    </Button>
                    <Button
                      onClick={() => handleHealDoubleClaimSingle(false)}
                      disabled={isHealingSingleDC || !singleDcUsnInput.trim()}
                      size="sm"
                      className="bg-red-700 hover:bg-red-800"
                      data-testid="heal-double-claim-single-apply-btn"
                    >
                      Revert
                    </Button>
                  </div>
                  {singleDcResult && (
                    <div className={`mt-2 p-3 rounded-lg border text-xs ${singleDcResult.dry_run ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                      <pre className="text-[10px] overflow-x-auto">{JSON.stringify(singleDcResult, null, 2)}</pre>
                    </div>
                  )}
                </div>

                {/* 4. BULK REPRINT CONTAMINATED USNs (2026-05-26) */}
                <div className="border rounded-md p-3 bg-white" data-testid="bulk-reprint-contaminated-section">
                  <p className="text-xs font-medium text-red-800 mb-2">
                    4. Bulk reprint contaminated USNs — mint fresh codes via the FG Reprint pipeline
                  </p>

                  {/* Load button — fetches already-healed USNs awaiting reprint */}
                  <div className="flex items-center gap-2 mb-2">
                    <Button
                      size="sm"
                      onClick={handleLoadPendingReprintList}
                      disabled={isLoadingPendingReprint}
                      className="bg-rose-600 hover:bg-rose-700"
                      data-testid="load-pending-reprint-btn"
                    >
                      {isLoadingPendingReprint
                        ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />Loading…</>
                        : <><Database className="w-3.5 h-3.5 mr-1" />Load Pending Reprint List</>}
                    </Button>
                    {pendingReprintList && (
                      <Badge className="bg-rose-100 text-rose-800 border-rose-300 text-[10px]" data-testid="pending-reprint-count-badge">
                        {pendingReprintList.healed_count} contaminated · awaiting fresh codes
                      </Badge>
                    )}
                    {!pendingReprintList && bulkReprintRows.length > 0 && (
                      <Badge variant="outline" className="text-[10px]">
                        Showing live-audit list ({bulkReprintRows.length})
                      </Badge>
                    )}
                  </div>

                  {!bulkReprintRows.length ? (
                    <p className="text-xs text-gray-500">
                      Click <strong>Load Pending Reprint List</strong> to fetch USNs that have already been
                      healed (status <code>contaminated_double_claim</code>) and need fresh codes. Tick the
                      ones you want to reprint and click <strong>Send to Print Queue</strong>. The FG Reprint
                      page will open with Phase A (pricing + product template + product printer) and Phase B
                      (box template + printer) for combo items.
                    </p>
                  ) : (
                    <>
                      <p className="text-[10px] text-gray-500 mb-1">
                        Source: <span className="font-medium">{bulkSourceLabel}</span>
                      </p>
                      <div className="flex items-center gap-2 flex-wrap mb-2 text-xs">
                        <span className="font-medium">
                          {bulkReprintSelected.size}/{bulkReprintRows.length} selected
                        </span>
                        <Button
                          size="sm" variant="outline"
                          onClick={selectAllVisible}
                          data-testid="bulk-reprint-select-visible-btn"
                        >
                          Select {bulkVisibleRows.length} visible
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={selectAllContaminated}
                          data-testid="bulk-reprint-select-all-btn"
                        >
                          Select all ({bulkReprintRows.length})
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={clearBulkSelection}
                          disabled={!bulkReprintSelected.size}
                          data-testid="bulk-reprint-clear-btn"
                        >
                          Clear
                        </Button>
                        <div className="ml-auto flex items-center gap-2">
                          <Label className="text-xs">Batch size:</Label>
                          <Select
                            value={String(bulkReprintBatchSize)}
                            onValueChange={(v) => { setBulkReprintBatchSize(Number(v)); setBulkReprintPage(0); }}
                          >
                            <SelectTrigger className="h-8 w-[80px] text-xs" data-testid="bulk-reprint-batch-size">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[10, 25, 50, 100].map(n => (
                                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="border rounded max-h-[300px] overflow-auto bg-gray-50">
                        <Table>
                          <TableHeader className="sticky top-0 bg-white">
                            <TableRow>
                              <TableHead className="w-[40px]"></TableHead>
                              <TableHead>USN</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Consumed In</TableHead>
                              <TableHead>Claim Reason</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {bulkVisibleRows.map((r) => {
                              const isCombo = String(r.usn_code || '').toUpperCase().startsWith('CMB-')
                                || /combo/i.test(r.operationally_consumed_in || '');
                              return (
                                <TableRow key={r.usn_code} data-testid={`bulk-reprint-row-${r.usn_code}`}>
                                  <TableCell>
                                    <Checkbox
                                      checked={bulkReprintSelected.has(r.usn_code)}
                                      onCheckedChange={() => toggleBulkSelected(r.usn_code)}
                                      data-testid={`bulk-reprint-checkbox-${r.usn_code}`}
                                    />
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">{r.usn_code}</TableCell>
                                  <TableCell>
                                    {isCombo ? (
                                      <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-[10px]">Combo</Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-[10px]">Single</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-[10px] text-gray-600">{r.operationally_consumed_in || '-'}</TableCell>
                                  <TableCell className="text-[10px] text-gray-500 truncate max-w-[200px]">{r.claim_reason || '-'}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>

                      <div className="flex items-center justify-between mt-2 text-xs">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setBulkReprintPage(p => Math.max(0, p - 1))}
                            disabled={bulkReprintPage === 0}
                            data-testid="bulk-reprint-prev-page-btn"
                          >Prev</Button>
                          <span className="text-gray-600">
                            Page {bulkReprintPage + 1} of {bulkTotalPages} ({bulkPageStart + 1}-{Math.min(bulkPageEnd, bulkReprintRows.length)} of {bulkReprintRows.length})
                          </span>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setBulkReprintPage(p => Math.min(bulkTotalPages - 1, p + 1))}
                            disabled={bulkReprintPage >= bulkTotalPages - 1}
                            data-testid="bulk-reprint-next-page-btn"
                          >Next</Button>
                        </div>
                        <Button
                          onClick={handleSendToReprint}
                          disabled={bulkReprintSelected.size === 0}
                          className="bg-indigo-600 hover:bg-indigo-700"
                          size="sm"
                          data-testid="bulk-reprint-send-btn"
                        >
                          <Printer className="w-4 h-4 mr-1" />
                          Send {bulkReprintSelected.size} to Print Queue
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Release Stuck Bundles from Cancelled Slips */}
            <Card data-testid="tool-release-cancelled-slip-bundles">
              <CardContent className="p-4 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Unlock className="w-4 h-4 text-red-600" />
                  Release Stuck Bundles (Cancelled Slips)
                </h3>
                <p className="text-xs text-muted-foreground">
                  Finds bundles stuck in IN_WIP or ALLOCATED referencing CANCELLED slips.
                  Re-allocates to pending slips if available, otherwise releases to STORED.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleReleaseStuckBundles(true)}
                    disabled={isReleasingStuck}
                    variant="outline"
                    size="sm"
                    data-testid="release-stuck-preview-btn"
                  >
                    {isReleasingStuck ? 'Scanning...' : 'Preview'}
                  </Button>
                  <Button
                    onClick={() => handleReleaseStuckBundles(false)}
                    disabled={isReleasingStuck || !stuckBundleResult?.stuck_count}
                    size="sm"
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="release-stuck-apply-btn"
                  >
                    {isReleasingStuck ? 'Releasing...' : `Release ${stuckBundleResult?.stuck_count || 0} Bundles`}
                  </Button>
                </div>
                {stuckBundleResult && (
                  <div className={`mt-2 p-3 rounded-lg border ${stuckBundleResult.dry_run ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                    {stuckBundleResult.dry_run ? (
                      <div className="text-xs text-amber-800 space-y-1">
                        <p className="font-medium">Preview Results:</p>
                        <p className="font-semibold text-red-700">Stuck bundles found: {stuckBundleResult.stuck_count}</p>
                        {stuckBundleResult.will_reallocate > 0 && (
                          <p className="text-blue-700 font-semibold">{stuckBundleResult.will_reallocate} bundle(s) will be re-allocated to pending slips</p>
                        )}
                        {stuckBundleResult.will_release_to_stored > 0 && (
                          <p>{stuckBundleResult.will_release_to_stored} bundle(s) will be released to STORED</p>
                        )}
                        <p className="text-gray-500">Across {stuckBundleResult.slips_with_stuck} cancelled slip(s)</p>
                        {stuckBundleResult.sample_bundles?.length > 0 && (
                          <div className="mt-2 max-h-48 overflow-y-auto">
                            <p className="font-medium mb-1">Sample bundles:</p>
                            {stuckBundleResult.sample_bundles.map(b => (
                              <p key={b.bundle_code} className="text-xs text-amber-700 font-mono">
                                {b.bundle_code} — Stuck on: {b.stuck_on_slip} — Bin: {b.bin_location}
                                {b.will_reallocate_to && <span className="text-blue-600 font-semibold"> → Re-allocate to {b.will_reallocate_to}</span>}
                              </p>
                            ))}
                            {stuckBundleResult.stuck_count > 50 && (
                              <p className="text-xs text-amber-500 mt-1">... and {stuckBundleResult.stuck_count - 50} more</p>
                            )}
                          </div>
                        )}
                        {stuckBundleResult.stuck_count === 0 && (
                          <p className="text-green-700 font-medium mt-1">No stuck bundles found. All clear!</p>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-green-800">
                        <p className="font-medium">{stuckBundleResult.message}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

          </TabsContent>
        </Tabs>
      </main>
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Staff</DialogTitle>
            <DialogDescription>
              Assign slip {selectedSlip?.slip_id} to a staff member
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Staff</Label>
            <Select value={selectedStaffForAssign} onValueChange={setSelectedStaffForAssign}>
              <SelectTrigger className="mt-2" data-testid="assign-staff-select">
                <SelectValue placeholder="Select staff..." />
              </SelectTrigger>
              <SelectContent>
                {availableStaff.map(staff => (
                  <SelectItem 
                    key={staff.staff_id} 
                    value={staff.staff_id}
                  >
                    {staff.staff_name || staff.staff_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignStaff} disabled={!selectedStaffForAssign} data-testid="confirm-assign-button">
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Staff Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Slip</DialogTitle>
            <DialogDescription>
              Reassign slip {selectedSlip?.slip_id} from {selectedSlip?.assigned_to_name || selectedSlip?.assigned_to} to another staff member.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-orange-700">
                <strong>Current Assignment:</strong> {selectedSlip?.assigned_to_name || selectedSlip?.assigned_to || 'None'}
              </p>
              <p className="text-sm text-orange-600 mt-1">
                {selectedSlip?.total_pieces} pieces · {selectedSlip?.total_designs} designs
              </p>
            </div>
            <Label>Select New Staff</Label>
            <Select value={selectedStaffForReassign} onValueChange={setSelectedStaffForReassign}>
              <SelectTrigger className="mt-2" data-testid="reassign-staff-select">
                <SelectValue placeholder="Select new staff..." />
              </SelectTrigger>
              <SelectContent>
                {availableStaff
                  .filter(staff => staff.staff_id !== selectedSlip?.assigned_to)
                  .map(staff => (
                    <SelectItem 
                      key={staff.staff_id} 
                      value={staff.staff_id}
                    >
                      {staff.staff_name || staff.staff_id}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleReassignStaff} 
              disabled={!selectedStaffForReassign}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="confirm-reassign-button"
            >
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slip Detail Dialog */}
      <Dialog open={slipDetailOpen} onOpenChange={setSlipDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Put Away Slip: {viewingSlip?.slip_id}
            </DialogTitle>
            <DialogDescription>
              {viewingSlip?.total_pieces} pieces · {viewingSlip?.total_designs} designs · Status: {viewingSlip?.status}
            </DialogDescription>
          </DialogHeader>
          
          {viewingSlip && (
            <div className="space-y-4">
              {/* Slip Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Assigned To</p>
                  <p className="font-medium">{viewingSlip.assigned_to_name || viewingSlip.assigned_to || 'Not assigned'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium">{new Date(viewingSlip.created_at).toLocaleString()}</p>
                </div>
              </div>
              
              {/* Items */}
              <div>
                <h4 className="font-semibold mb-2">Items ({viewingSlip.items?.length || 0})</h4>
                <div className="space-y-3">
                  {viewingSlip.items?.map((item, idx) => (
                    <div key={item.item_id} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-lg">{item.design_name}</p>
                          <p className="text-sm text-gray-600">
                            {item.shade && <span className="mr-2">Shade: {item.shade}</span>}
                            {item.size && <span className="mr-2">Size: {item.size}</span>}
                          </p>
                        </div>
                        <Badge className={item.velocity_class === 'A' ? 'bg-red-100 text-red-800' : item.velocity_class === 'B' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100'}>
                          {item.velocity_class || 'C'}
                        </Badge>
                      </div>
                      
                      {item.description && (
                        <p className="text-sm text-gray-500 mb-2">{item.description}</p>
                      )}
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div className="bg-white p-2 rounded">
                          <p className="text-gray-500 text-xs">Barcode</p>
                          <p className="font-mono text-sm">{item.barcode || '-'}</p>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <p className="text-gray-500 text-xs">Qty</p>
                          <p className="font-semibold">{item.qty} pcs</p>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <p className="text-gray-500 text-xs">Processed By</p>
                          <p className="font-medium">{item.processed_by_name || item.processed_by_staff || '-'}</p>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <p className="text-gray-500 text-xs">Suggested Bin</p>
                          <p className="font-mono">{item.suggested_bin || 'Any'}</p>
                        </div>
                      </div>
                      
                      {item.status !== 'pending' && (
                        <div className="mt-2 pt-2 border-t">
                          <p className="text-xs text-gray-500">
                            Status: <span className={item.status === 'completed' ? 'text-green-600' : 'text-blue-600'}>{item.status}</span>
                            {item.actual_bin && <span className="ml-2">| Actual Bin: {item.actual_bin}</span>}
                            {item.placed_usns?.length > 0 && <span className="ml-2">| Placed: {item.placed_usns.length}/{item.qty}</span>}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlipDetailOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Skipped Item Action Dialog - Reassign */}
      <Dialog open={skippedActionDialog === 'reassign'} onOpenChange={() => setSkippedActionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-blue-600" />
              Reassign Skipped Item
            </DialogTitle>
            <DialogDescription>
              Create a new put away slip and assign to available staff.
            </DialogDescription>
          </DialogHeader>
          
          {selectedSkippedItem && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Design:</span>
                  <span className="font-medium">{selectedSkippedItem.design_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Shade/Size:</span>
                  <span>{selectedSkippedItem.shade} / {selectedSkippedItem.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Skipped Qty:</span>
                  <span className="font-bold text-amber-600">{selectedSkippedItem.skipped_qty} pcs</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Reason:</span>
                  <span className="text-amber-700">{selectedSkippedItem.reason}</span>
                </div>
              </div>
              
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={skippedActionNotes}
                  onChange={(e) => setSkippedActionNotes(e.target.value)}
                  placeholder="Add notes about the correction made..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSkippedActionDialog(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReassignSkipped}
              disabled={isProcessingSkipped}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isProcessingSkipped ? 'Processing...' : 'Create New Slip & Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Skipped Item Action Dialog - Return to SFG */}
      <Dialog open={skippedActionDialog === 'return_sfg'} onOpenChange={() => setSkippedActionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-green-600" />
              Return to SFG Storage
            </DialogTitle>
            <DialogDescription>
              Create a new bundle and put away in SFG storage.
            </DialogDescription>
          </DialogHeader>
          
          {selectedSkippedItem && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Design:</span>
                  <span className="font-medium">{selectedSkippedItem.design_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Shade/Size:</span>
                  <span>{selectedSkippedItem.shade} / {selectedSkippedItem.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Pieces to Return:</span>
                  <span className="font-bold text-green-600">{selectedSkippedItem.skipped_qty} pcs</span>
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-700">
                  <strong>New bundle</strong> will be created and system will suggest a bin for SFG put away.
                </p>
              </div>
              
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={skippedActionNotes}
                  onChange={(e) => setSkippedActionNotes(e.target.value)}
                  placeholder="Add notes about why returning to SFG..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSkippedActionDialog(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReturnToSFG}
              disabled={isProcessingSkipped}
              className="bg-green-600 hover:bg-green-700"
            >
              {isProcessingSkipped ? 'Processing...' : 'Create Bundle & Return to SFG'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset In-Progress Slips Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <RotateCcw className="w-5 h-5" />
              Reset In-Progress Slips
            </DialogTitle>
            <DialogDescription>
              Reset stuck in-progress slips back to pending for reassignment
            </DialogDescription>
          </DialogHeader>

          {isLoadingInProgress ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2">Loading...</span>
            </div>
          ) : inProgressSummary ? (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <Card className="bg-purple-50">
                  <CardContent className="py-3 text-center">
                    <p className="text-2xl font-bold text-purple-700">{inProgressSummary.total_in_progress}</p>
                    <p className="text-xs text-purple-600">Total In-Progress</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50">
                  <CardContent className="py-3 text-center">
                    <p className="text-2xl font-bold text-orange-700">{inProgressSummary.stuck_slips?.length || 0}</p>
                    <p className="text-xs text-orange-600">Stuck (&gt;2 hrs)</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50">
                  <CardContent className="py-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">{inProgressSummary.total_pieces}</p>
                    <p className="text-xs text-blue-600">Total Pieces</p>
                  </CardContent>
                </Card>
              </div>

              {/* Reset Options */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-amber-800">Reset All Stuck Slips</p>
                    <p className="text-sm text-amber-600">
                      Reset slips that have been in-progress for more than {resetHours} hours
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={resetHours.toString()} onValueChange={(v) => setResetHours(parseInt(v))}>
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 hr</SelectItem>
                        <SelectItem value="2">2 hrs</SelectItem>
                        <SelectItem value="4">4 hrs</SelectItem>
                        <SelectItem value="8">8 hrs</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => handleResetInProgressSlips()}
                      disabled={isResetting || (inProgressSummary.stuck_slips?.length || 0) === 0}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {isResetting ? 'Resetting...' : `Reset ${inProgressSummary.stuck_slips?.length || 0} Slips`}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Stuck Slips List */}
              {inProgressSummary.stuck_slips?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Stuck Slips (older than 2 hours)</h4>
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Slip ID</TableHead>
                          <TableHead>Staff</TableHead>
                          <TableHead>Pieces</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead>Hours</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inProgressSummary.stuck_slips.map((slip) => (
                          <TableRow key={slip.slip_id}>
                            <TableCell className="font-mono text-sm">{slip.slip_id}</TableCell>
                            <TableCell>{slip.staff_name}</TableCell>
                            <TableCell>{slip.total_pieces}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {slip.completed_items}/{slip.total_items} ({slip.progress_pct}%)
                              </Badge>
                            </TableCell>
                            <TableCell className="text-orange-600 font-medium">
                              {slip.hours_since_assigned?.toFixed(1)}h
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResetInProgressSlips(slip.slip_id)}
                                disabled={isResetting}
                                className="text-purple-600 hover:text-purple-700"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Active Slips (recent) */}
              {inProgressSummary.active_slips?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Active Slips (being worked on)</h4>
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Slip ID</TableHead>
                          <TableHead>Staff</TableHead>
                          <TableHead>Pieces</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inProgressSummary.active_slips.slice(0, 10).map((slip) => (
                          <TableRow key={slip.slip_id}>
                            <TableCell className="font-mono text-sm">{slip.slip_id}</TableCell>
                            <TableCell>{slip.staff_name}</TableCell>
                            <TableCell>{slip.total_pieces}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                                {slip.completed_items}/{slip.total_items} ({slip.progress_pct}%)
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResetInProgressSlips(slip.slip_id)}
                                disabled={isResetting}
                                className="text-gray-400 hover:text-purple-600"
                                title="Force reset this slip"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {inProgressSummary.active_slips.length > 10 && (
                      <p className="text-xs text-gray-500 p-2 text-center">
                        ... and {inProgressSummary.active_slips.length - 10} more active slips
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* No slips message */}
              {inProgressSummary.total_in_progress === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>No in-progress slips to reset</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Failed to load data
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FGPutAwaySupervisor;
